/* eslint-disable */
import type { GlobalConfig } from "../cash/globalconfig/global_config.pb";
import type { GroupParticipant } from "../cash/groups/group.pb";
import type { SubmitAutomationForFlowRequest } from "../cash/investautomator/api/flows/submitAutomationForFlow.pb";
import type { SubmitInvestmentEntitySelectionRequest } from "../cash/investflow/flows/actions.pb";
import type { SelectInstrumentRequest } from "../cash/moneta/api/v1_0/plasma/app.pb";
import type { CallContext } from "../cash/plasma/common/CallContext.pb";
import type { FlowType } from "../cash/plasma/flows/FlowProto.pb";
import type { WebViewCallbackInputs } from "../cash/plasma/ui/inputs/BlockerInputsProto.pb";
import type { SetPasswordRequest, VerifyPasswordRequest } from "../cash/security/api.pb";
import type { Image } from "../cash/ui/Image.pb";
import type { Country } from "../common/countries.pb";
import type { Money } from "../common/currency.pb";
import type { CurrencyCode } from "../common/currency_codes.pb";
import type { GlobalAddress } from "../common/location.pb";
import type { GiftCardPaymentData } from "../giftly/gift_card.pb";
import type { DirectDepositTransferSelectionRequest } from "../instant-pay/instantpay_api.pb";
import type { GpsEvent } from "../locator/service/app/location/GpsEventProto.pb";
import type { CashInRequest } from "../moneta/api/CashInRequestProto.pb";
import type { CashInResponse } from "../moneta/api/CashInResponseProto.pb";
import type { PayrollProvider } from "../payroll-connector/common/PayrollProviderProto.pb";
import type { RecurringSchedule, ScheduledTransactionPreference } from "../repeatedly/common.pb";
import type { GetTransferRequest } from "./GetTransferRequestProto.pb";
import type { GetTransferResponse } from "./GetTransferResponseProto.pb";
import type { InitiatePasscodeResetRequest } from "./InitiatePasscodeResetRequestProto.pb";
import type { InitiatePasscodeResetResponse } from "./InitiatePasscodeResetResponseProto.pb";
import type { Profile } from "./ProfileProto.pb";
import type { RequestContext } from "./RequestContextProto.pb";
import type { ResponseContext } from "./ResponseContextProto.pb";
import type { SetDepositPreferenceRequest } from "./SetDepositPreferenceRequestProto.pb";
import type { SetDepositPreferenceResponse } from "./SetDepositPreferenceResponseProto.pb";
import type { SetNotificationPreferenceRequest } from "./SetNotificationPreferenceRequestProto.pb";
import type { SetNotificationPreferenceResponse } from "./SetNotificationPreferenceResponseProto.pb";
import type { SetPasscodeRequest } from "./SetPasscodeRequestProto.pb";
import type { SetPasscodeResponse } from "./SetPasscodeResponseProto.pb";
import type { SyncEntitiesRequest } from "./SyncEntitiesRequestProto.pb";
import type { SyncEntitiesResponse } from "./SyncEntitiesResponseProto.pb";
import type { TransferFundsRequest } from "./TransferFundsRequestProto.pb";
import type { TransferFundsResponse } from "./TransferFundsResponseProto.pb";
import type { UiAlias } from "./UiAliasProto.pb";
import type { UnlinkInstrumentRequest } from "./UnlinkInstrumentRequestProto.pb";
import type { UnlinkInstrumentResponse } from "./UnlinkInstrumentResponseProto.pb";
import type {
  BalanceSnapshot,
  Blockers,
  CampaignSupporterData,
  CardDesignBlockerCardTheme,
  CashCustomer,
  CashCustomerProfile,
  CashtagStatus,
  ClientScenario,
  Instrument,
  InstrumentSelection,
  PostalAddress,
  ScientificNumber,
  StatementTransaction,
  SupportConfig,
  TaxEntity,
} from "./api.pb";
import type { AppMessagePayload } from "./app_messaging.pb";
import type {
  AuthenticateInstitutionRequest,
  AuthenticateInstitutionResponse,
  InstitutionsConfig,
  SelectInstitutionAccountRequest,
  SelectInstitutionAccountResponse,
  VerifyMultifactorAuthenticationRequest,
  VerifyMultifactorAuthenticationResponse,
} from "./bankbook.pb";
import type {
  MultiCurrencyAmountEntryBlockerCostTierFee,
  MultiCurrencyAmountEntryBlockerFocusedCurrencyField,
} from "./blockers/multi_currency_amount_entry_blocker.pb";
import type { CardCustomizationDataCustomizationArea, TouchData } from "./cards.pb";
import type { CashInstrumentType } from "./cash_instrument_type.pb";
import type {
  ApplePayProvisioningResult,
  AppleWatchConfig,
  AppVersion,
  BankAccount,
  BitcoinDisplayUnits,
  BlockersConfig,
  BoostConfig,
  BuildType,
  CardTheme,
  CashDrawerConfig,
  Conversation,
  CryptocurrencyConfig,
  CustomerVerificationData,
  DeviceLocationHeuristics,
  DigitalWalletTokenProvisioningCompletionData,
  DigitalWalletTokenProvisioningRequestData,
  DigitalWalletTokenProvisioningResponseData,
  ExchangeContract,
  ExchangeData,
  FeatureFlag,
  FieldName,
  HashedContact,
  InstrumentLinkingConfig,
  IssuedCard,
  KeyedCard,
  LimitedAction,
  MarketCapabilitiesConfig,
  OcrConfig,
  OfflineConfig,
  PaymentHistoryConfig,
  PaymentRewardStatus,
  PriceHistory,
  RecipientConfig,
  RewardsData,
  RewardStatus,
  ServiceMessage,
  SessionStatus,
  SharingConfig,
  SmsDeliveryMechanism,
  StampsConfig,
  SyncedContact,
  ThreadedViewData,
  TreehouseConfig,
  WebLoginConfig,
} from "./common.pb";
import type { FileCategory } from "./file_category.pb";
import type { HistoricalRange } from "./investing/common.pb";
import type { CustomOrder, InvestPaymentData, StatementType } from "./investing/resources.pb";
import type { NearbyVisibility } from "./nearby_visibility.pb";
import type { Orientation } from "./orientation.pb";
import type { PaymentScheduleState, ScheduleRFC2445 } from "./payment_schedule.pb";
import type { ResolvePersonaDidvBlockerRequest, ResolvePersonaDidvBlockerResponse } from "./persona_didv.pb";
import type { IncomingRequestPolicy } from "./privacy/policies.pb";
import type { RatePlan } from "./rate_plan.pb";
import type { Reaction } from "./reaction.pb";
import type { Region } from "./region.pb";
import type { CheckDepositRequest, CheckDepositResponse } from "./rpc/check_deposit.pb";
import type {
  HandleThreeDomainSecureRedirectRequest,
  HandleThreeDomainSecureRedirectResponse,
} from "./rpc/handle_three_domain_secure_redirect.pb";
import type {
  HandleThreeDomainSecureV2ActionRequest,
  HandleThreeDomainSecureV2ActionResponse,
} from "./rpc/handle_three_domain_secure_v2_action.pb";
import type {
  InvitationConfig,
  InvitationConfigInvitationTreatment,
  OAuthConfigFlowType,
  PlaidLinkingConfig,
  RatePlanConfig,
  ScenarioPlan,
} from "./scenarios.pb";
import type { AppAttestAttestation, PlayIntegrityAttestation, SafetyNetAttestation } from "./signal.pb";
import type { StatusResult } from "./status_result.pb";
import type {
  AdvanceSupportFlowRequest,
  AdvanceSupportFlowResponse,
  CreateSupportCaseRequest,
  CreateSupportCaseResponse,
  GetSupportContactStatusRequest,
  GetSupportContactStatusResponse,
  ReportSupportFlowActivityRequest,
  ReportSupportFlowActivityResponse,
  StartSupportFlowRequest,
  StartSupportFlowResponse,
} from "./support.pb";
import type { SyncEntity } from "./sync_entity.pb";
import type { Avatar } from "./ui.pb";
import type { UiCustomer } from "./ui/customer.pb";
import type { ThreadedPaymentReply, UiPayment } from "./ui/payment.pb";
import type { UiPaymentSchedule } from "./ui/payment_schedule.pb";
import type {
  PinwheelLinkOnExitResponse,
  PinwheelLinkOnLoginResponse,
  PinwheelLinkOnSuccessResponse,
} from "./ui/pinwheel_link.pb";

/** In-App activities, screens, or components that can result in payments being created. */
export enum AppCreationActivity {
  /** HOMESCREEN - The payment was initiated after the customer selected an amount on the home screen. */
  HOMESCREEN = "HOMESCREEN",
  /** DEEPLINK - The payment was initiated after the customer opened a deep link or scanned a QR code. */
  DEEPLINK = "DEEPLINK",
  /** IN_APP_SCANNER - The payment was initiated after the customer scanned a QR code using the in-app scanner (Android only). */
  IN_APP_SCANNER = "IN_APP_SCANNER",
  /** THREADED_PROFILE - The payment was initiated from a threaded customer profile in Activity. */
  THREADED_PROFILE = "THREADED_PROFILE",
  /**
   * HOMESCREEN_SEARCH_BAR - Deprecated. Previously used for an experiment that allowed the customer to initiate a payment
   * via a recipient search bar on the home screen.
   *
   * @deprecated
   */
  HOMESCREEN_SEARCH_BAR = "HOMESCREEN_SEARCH_BAR",
  /** SIRI - The payment was initiated in Siri (iOS only). */
  SIRI = "SIRI",
  /** IMESSAGE - The payment was initiated in iMessage (iOS only). */
  IMESSAGE = "IMESSAGE",
  /** DEVICE_HOMESCREEN_ACTION - The payment was initiated from a quick action on the device's home screen (iOS only). */
  DEVICE_HOMESCREEN_ACTION = "DEVICE_HOMESCREEN_ACTION",
  /**
   * BITCOIN_WITHDRAWAL - The payment was initiated after the customer opened the Bitcoin withdrawal screen
   * and selected a customer instead of pasting a Bitcoin wallet address.
   */
  BITCOIN_WITHDRAWAL = "BITCOIN_WITHDRAWAL",
  /** ACTIVITY_CUSTOMER_PROFILE - The payment was initiated from the customer profile screen in Activity. */
  ACTIVITY_CUSTOMER_PROFILE = "ACTIVITY_CUSTOMER_PROFILE",
  /** PROFILE_DIRECTORY - The payment was initiated from Profile Directory launched from Home screen. */
  PROFILE_DIRECTORY = "PROFILE_DIRECTORY",
}

/** Claimable Payment status. */
export enum ClaimStatus {
  NEW = "NEW",
  CLAIMED = "CLAIMED",
}

/**
 * For supported clients: the first call a client makes when it has no valid session. It returns
 * a new session, and also provides an app token if the client did not include one in its headers.
 */
export interface InitiateSessionRequest {
  /**
   * Deprecated and moved to security context.
   *
   * @deprecated
   */
  device_check_token?: Uint8Array;
  /**
   * Backup Tags are tokens used to identify related client backups (i.e. restoring from backup on a
   * new smartphone). If the client's backup store does not contain a Backup Tag, this is null.
   */
  backup_tag?: string;
  /**
   * The clients security / privacy context information. This data is currently used in client
   * app-level encryption and hardware attestation.
   */
  client_security_context?: ClientSecurityContext;
  /**
   * Deprecated as we starts using device_locale_country instead
   * The device locales. This data is used to guess the region of the user before login
   * eg. "es-AU, fr-AU, en-US, pl-AU"
   *
   * @deprecated
   */
  device_locales?: string;
  /**
   * The device locale country based on device region setting.
   * This data is used to guess the region of the user before login
   * eg. "AU", "US". This data is used to guess the region of the user before login
   */
  device_locale_country?: string;
}

export interface ClientSecurityContext {
  /**
   * The clients public key, used to send and persist encrypted data on the device.
   * This is an ECIES tink keyset, proto encoded.
   */
  client_public_key?: Uint8Array;
  /** Device token for iOS Device Check integration, as in RequestContext. */
  device_check_token?: Uint8Array;
}

/** Config for the client to render and navigate the account list. */
export interface AccountListConfig {
  accounts?: AccountListConfigAccount[];
  /** Associated client_scenario, in this case it would be LOGIN. */
  client_scenario?: ClientScenario;
}

export interface AccountListConfigAccount {
  /** A piece of this account's data (e.g. full name, cashtag) used as the "title". This field is never null. */
  title?: string;
  /** A piece of this account's data (e.g. full name, cashtag) used as the "subtitle". This field is never null. */
  subtitle?: string;
  /** This account's avatar. This field is never null. */
  avatar?: Avatar;
  /** Aliases that can be chosen to send a challenge to. */
  aliases?: UiAlias[];
  /** If present, bypass alias selection and send the challenge here. */
  preferred_alias?: UiAlias;
  account_id?: string;
  /**
   * This account's cashtag, may be null. Should be used in cases where only displaying the cashtag makes sense;
   * in other cases the apps should use UI-specific fields, such as "title" and "subtitle".
   */
  cash_tag?: string;
  region?: Region;
  /** Whether if this account has registered for passkey login. */
  is_passkey_registered?: boolean;
}

/** Config for the client to render and navigate the account list. */
export interface ProfileListConfig {
  profiles?: ProfileListConfigProfile[];
  /** Associated client_scenario, in this case it would be LOGIN. */
  client_scenario?: ClientScenario;
}

export interface ProfileListConfigProfile {
  /** A piece of this account's data (e.g. full name, cashtag) used as the "title". This field is never null. */
  title?: string;
  /** A piece of this account's data (e.g. full name, cashtag) used as the "subtitle". This field is never null. */
  subtitle?: string;
  /** This account's avatar. This field is never null. */
  avatar?: Avatar;
  /** Aliases that can be chosen to send a challenge to. */
  aliases?: UiAlias[];
  /** If present, bypass alias selection and send the challenge here. */
  preferred_alias?: UiAlias;
  profile_id?: string;
  /**
   * This account's cashtag, may be null. Should be used in cases where only displaying the cashtag makes sense;
   * in other cases the apps should use UI-specific fields, such as "title" and "subtitle".
   */
  cash_tag?: string;
  region?: Region;
  /** Whether if this account has registered for passkey login. */
  is_passkey_registered?: boolean;
  is_business?: boolean;
}

/** App intro data/configuration. */
export interface AppIntroData {
  action_button_title?: string;
  screen_content?: AppIntroDataScreenContent[];
  guessed_region?: Region;
}

export enum AppIntroDataPanel {
  PANEL1 = "PANEL1",
  PANEL2 = "PANEL2",
  PANEL3 = "PANEL3",
  PANEL4 = "PANEL4",
}

export interface AppIntroDataScreenContent {
  /**
   * An identifier for reporting screen content interactions
   * eg: P2P, BITCOIN, DIRECT_DEPOSIT, CASH_CARD, etc.
   */
  content_id?: string;
  /** An identifier associated with a bundled background asset on the mobile clients */
  panel?: AppIntroDataPanel;
  /** A url for an image that will be loaded into the screen. */
  content_image_url?: string;
  title_text?: string;
}

export interface InitiateSessionResponse {
  session_token?: string;
  /** An explicit status of the client's session. With current logic this will always be PARTIAL. */
  session_status?: SessionStatus;
  /**
   * A token to identify the app in subsequent requests. If the current request included one, then
   * this is generally null. If the app version supports it (Android: 3.45+, iOS: TBD),
   * the server may return a new app token that the client should use to replace its existing one.
   */
  app_token?: string;
  /** Login-specific feature flags, provided here before there is a fully authenticated customer. */
  login_feature_flags?: FeatureFlag[];
  /**
   * If not null, this is the new Backup Tag that the client should write to the backup store,
   * discarding the current Backup Tag.
   */
  backup_tag?: string;
  /**
   * Returned for Android clients to initiate device verification.
   *
   * Deprecated in 3.93. Use play_integrity_nonce.
   *
   * @deprecated
   */
  safety_net_nonce?: string;
  /**
   * SafetyNet API key to use.
   * The client already has an API key baked into it,
   * this enables us to be able to update it from the server.
   *
   * Deprecated in 3.93.
   *
   * @deprecated
   */
  safety_net_api_key?: string;
  /**
   * Optional screen_config for the account selector screen.
   * This will contain all pertinent information to populate the account selector screen
   */
  account_list_config?: AccountListConfig;
  /** Optional app intro screen data/configuration. */
  app_intro_data?: AppIntroData;
  /** Returned for Android clients to initiate device verification via Play Integrity. */
  play_integrity_nonce?: string;
  /** This will contain all pertinent information of related customer profiles */
  profile_list_config?: ProfileListConfig;
  /** Returned for iOS clients to initiate app attestation via App Attest. */
  app_attest_challenge?: string;
}

export interface GetLinkedAccountsRequest {
  /** App token for POC implementation. We will change to backup tags for MVP */
  app_token?: string;
  backup_tag?: string;
}

export interface GetLinkedAccountsResponse {
  /** This will contain all pertinent information to populate the account selector screen */
  account_list_config?: AccountListConfig;
  response_context?: ResponseContext;
  /** This will contain all pertinent information of related customer profiles */
  profile_list_config?: ProfileListConfig;
}

/**
 * Creates a customer's secondary profile, which could be p2p or c4b.
 * Also creates the internal account holder mapping in Janus
 */
export interface CreateSecondaryCustomerProfileRequest {
  rate_plan?: RatePlan;
  idempotence_token?: string;
}

export interface CreateSecondaryCustomerProfileResponse {
  /** The new customer that was just created */
  customer_token?: string;
}

export interface GetThreadedViewDataRequest {
  customer_id?: string;
}

export interface GetThreadedViewDataResponse {
  threaded_view_data?: ThreadedViewData;
}

/** The request to register an SMS number as the first step to authenticating it. */
export interface RegisterSmsRequest {
  request_context?: RequestContext;
  sms_number?: string;
  verification_mechanism?: SmsDeliveryMechanism;
  /**
   * Deprecated. Use client scenario header.
   *
   * @deprecated
   */
  scenario?: RegisterSmsRequestScenario;
  /** True if the phone number matches the phone number pre-filled using phone's information. */
  pre_filled_from_phone?: boolean;
  /** The payments being acted upon. */
  payment_tokens?: string[];
  /** If true, this request will fail if a new customer must be created for this alias to be claimed. */
  existing_customer_only?: boolean;
  device_location_heuristics?: DeviceLocationHeuristics;
  /** A client generated token that triggers a callback to the app when included in the SMS message. */
  app_callback_token?: string;
  /** Tags are out of order. Next tag = 13. */
  account_id?: string;
}

export enum RegisterSmsRequestScenario {
  /** ONBOARDING - The customer is registering their SMS as part of onboarding. */
  ONBOARDING = "ONBOARDING",
  /** PROFILE - The customer is registering their SMS from their profile. */
  PROFILE = "PROFILE",
}

export interface RegisterSmsResponse {
  response_context?: ResponseContext;
  status?: RegisterSmsResponseStatus;
}

export enum RegisterSmsResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The request was successful, an SMS has been sent. */
  SUCCESS = "SUCCESS",
  /**
   * INVALID_SMS_NUMBER - The SMS number provided did not conform to an acceptable format. The number must be a
   * 10-digit number (or 11 digits and start with a 1). All non-digit characters will be ignored.
   */
  INVALID_SMS_NUMBER = "INVALID_SMS_NUMBER",
  /**
   * TOO_MANY_REQUESTS - There have been too many requests to register this SMS number in the last 24 hours. The user
   * can try again later.
   */
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
  /**
   * DUPLICATE_SMS_NUMBER - The sms number is already associated with the requesting user.
   * Added for 2.11
   */
  DUPLICATE_SMS_NUMBER = "DUPLICATE_SMS_NUMBER",
  /** CUSTOMER_DOES_NOT_EXIST - No customer exists for this SMS number, and existing_customer_only was set in the request. */
  CUSTOMER_DOES_NOT_EXIST = "CUSTOMER_DOES_NOT_EXIST",
}

/** The request to verify an SMS number with a verification code. */
export interface VerifySmsRequest {
  request_context?: RequestContext;
  sms_number?: string;
  sms_verification_code?: string;
  /** The payments being acted upon. */
  payment_tokens?: string[];
  account_id?: string;
  /**
   * The clients security / privacy context information. This data is currently used in client
   * app-level encryption and hardware attestation. Provide this when session is to be minted
   * during verifySms.
   */
  client_security_context?: ClientSecurityContext;
}

/**
 * The response to a request to verify an SMS number.  It contains the session token used in other
 * app requests.
 */
export interface VerifySmsResponse {
  response_context?: ResponseContext;
  status?: VerifySmsResponseStatus;
  /** @deprecated */
  session_token?: string;
  /**
   * No longer used.
   *
   * @deprecated
   */
  customer_verification_data?: CustomerVerificationData;
}

export enum VerifySmsResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The request was successful. A session token has been provided. */
  SUCCESS = "SUCCESS",
  /** INVALID_SMS_NUMBER - The SMS number provided did not conform to an acceptable format or has not been registered. */
  INVALID_SMS_NUMBER = "INVALID_SMS_NUMBER",
  /** INVALID_VERIFICATION_CODE - The verification code is invalid. The user should retry with the correct verification code. */
  INVALID_VERIFICATION_CODE = "INVALID_VERIFICATION_CODE",
  /**
   * EXPIRED_VERIFICATION_CODE - The verification code has expired. The user should register their SMS again to get a new
   * verification code.
   */
  EXPIRED_VERIFICATION_CODE = "EXPIRED_VERIFICATION_CODE",
  /**
   * TOO_MANY_FAILED_ATTEMPTS - There have been too many failed attempts to verify this SMS number in the past 7 days.
   * The user can try again later.
   */
  TOO_MANY_FAILED_ATTEMPTS = "TOO_MANY_FAILED_ATTEMPTS",
  /** SUCCESS_WITH_VERIFICATION_DATA - No longer used. */
  SUCCESS_WITH_VERIFICATION_DATA = "SUCCESS_WITH_VERIFICATION_DATA",
  /** NOT_ELIGIBLE - Only for 2.5+ apps: this action was related to a customer merge that was deemed not eligible. */
  NOT_ELIGIBLE = "NOT_ELIGIBLE",
}

/**
 * The request to verify a passcode. Used in several situations:
 * 1. When signing in to a new app, the user is required to specify a CVV or have their cards
 *    be unlinked.
 * 2. When resetting a passcode, a user may be required to verify the CVV of their card.
 * 3. When enabling an issued card, a user with a passcode must provide it.
 * 4. When unlocking an issued card, a user with a passcode must provide it.
 */
export interface VerifyPasscodeRequest {
  request_context?: RequestContext;
  passcode?: string;
  /** A token in lieu of a passcode. Used for biometric authentication mechanisms. */
  passcode_token?: string;
}

/** The response to a request to verify the CVV for a card belonging to an existing customer. */
export interface VerifyPasscodeResponse {
  response_context?: ResponseContext;
  status?: VerifyPasscodeResponseStatus;
  full_name?: string;
  /**
   * A token that can be supplied when initiating a payment to skip passcode confirmation for
   * that payment.
   */
  passcode_token?: string;
  /** The instrument that was verified. Only populated on successful verifications. */
  instrument?: Instrument;
}

export enum VerifyPasscodeResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The CVV was correct, the card was successfully linked to this customer. */
  SUCCESS = "SUCCESS",
  /** INVALID_PASSCODE - The passcode is invalid. The user should retry with the passcode. */
  INVALID_PASSCODE = "INVALID_PASSCODE",
  /**
   * TOO_MANY_ATTEMPTS - The passcode is invalid and the user has exhausted the number of attempts to provide it.
   * The user should be prompted to link a new card.
   */
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
  /** CARD_BLOCKED - The card has been blocked. The user should be prompted to link a new card. */
  CARD_BLOCKED = "CARD_BLOCKED",
  /**
   * FAILURE - There are a lot of rare but possible failure cases (like the card was unlinked since the
   * verification token was created). The user should be prompted to link a new card.
   */
  FAILURE = "FAILURE",
}

/**
 * The request to verify an instrument, using card PAN or bank routing and account number.
 * This request should be sent to resolve an InstrumentVerificationBlocker.
 *
 * These fields match the card or bank number provided in LinkCard and LinkBankAccount.
 * The values are compared to a previous card or bank link, and are not used to link an
 * instrument.
 */
export interface VerifyInstrumentRequest {
  request_context?: RequestContext;
  /** Only set if prompted for a card. If set, routing_number and account_number should be null. */
  unencrypted_pan?: string;
  /**
   * Only set if prompted for a bank. If set, unencrypted_pan should be null.
   * The following two fields must either both be set or neither.
   */
  routing_number?: string;
  account_number?: string;
}

/** The response to a request to verify the full instrument belonging to an existing customer. */
export interface VerifyInstrumentResponse {
  response_context?: ResponseContext;
  status?: VerifyInstrumentResponseStatus;
}

export enum VerifyInstrumentResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The instrument matched. */
  SUCCESS = "SUCCESS",
  /** FAILURE - The instrument didn't match, the scenario_plan will possibly instruct the user to retry. */
  FAILURE = "FAILURE",
  /** TOO_MANY_ATTEMPTS - Attempted too many times. Try later. */
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
  /** CONCURRENT_MODIFICATION - The instrument no longer needs to be verified. */
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
}

/** The request to match a customer's contacts to existing Cash App customers. */
export interface MatchContactsRequest {
  hashed_contacts?: HashedContact[];
}

export interface MatchContactsResponse {
  status?: MatchContactsResponseStatus;
  matched_contacts?: HashedContact[];
}

export enum MatchContactsResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
}

/** The request to sync device contacts with the server. */
export interface SyncContactsRequest {
  /**
   * If not set, the server assumes the device is syncing from scratch and
   * removes any data currently stored regarding the contacts of this device before
   * starting the sync.
   * When re-syncing a currently synced device, use the sync token returned by
   * the last call to sync contacts.
   */
  sync_token?: string;
  /** List of hashed aliases added to the contact book of the device since last sync. */
  add_hashed_aliases?: Uint8Array[];
  /** List of hashed aliases removed from the contact book of the device since last sync. */
  remove_hashed_aliases?: Uint8Array[];
}

export interface SyncContactsResponse {
  status?: SyncContactsResponseStatus;
  /** Returns a token for that should be passed in subsequent calls to sync contacts. */
  sync_token?: string;
  /** Newly matched app contacts who are currently using Cash App. */
  add_contacts?: SyncedContact[];
  /** Hashed aliases of app contacts who stopped using Cash since last sync. */
  remove_hashed_aliases?: Uint8Array[];
}

export enum SyncContactsResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
}

export interface VerifyContactsRequest {
  /** hashed aliases of contacts to be verified against. */
  request_context?: RequestContext;
  hashed_aliases?: Uint8Array[];
}

export interface VerifyContactsResponse {
  response_context?: ResponseContext;
  status?: VerifyContactsResponseStatus;
}

export enum VerifyContactsResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
}

/** The request to delete a customer's contacts that were previously attempted to be matched. */
export interface DeleteContactsRequest {
  contact_ids?: string[];
}

export interface DeleteContactsResponse {
  status?: DeleteContactsResponseStatus;
}

export enum DeleteContactsResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** The request to refresh the session token. */
export interface RefreshSessionRequest {
  /**
   * Backup Tags are tokens used to identify related client backups (i.e. restoring from backup on a
   * new smartphone). If the client's backup store does not contain a Backup Tag, this is null.
   */
  backup_tag?: string;
}

export interface RefreshSessionResponse {
  status?: RefreshSessionResponseStatus;
  session_token?: string;
  /**
   * If not null, this is the new Backup Tag that the client should write to the backup store,
   * discarding the current Backup Tag.
   */
  backup_tag?: string;
  /**
   * Returned for Android clients that support it.
   *
   * Deprecated in 3.93. Use play_integrity_nonce.
   *
   * @deprecated
   */
  safety_net_nonce?: string;
  /**
   * SafetyNet API key to use.
   * The client already has an API key baked into it,
   * this enables us to be able to update it from the server.
   *
   * Deprecated in 3.93.
   *
   * @deprecated
   */
  safety_net_api_key?: string;
  /** Returned for Android clients to initiate device verification via Play Integrity. */
  play_integrity_nonce?: string;
  /** Returned for iOS clients to initiate app attestation via App Attest. */
  app_attest_challenge?: string;
}

export enum RefreshSessionResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Initiates sending of funds, or requests for funds, between customers. */
export interface InitiatePaymentRequest {
  request_context?: RequestContext;
  /** An external id provides idempotence if a request is retried. */
  external_id?: string;
  /**
   * There are two customers involved in every payment: the initiator and the getter.  Most
   * InitiatePaymentRequests have a single getter and, thus, create a single payment.  However, it
   * is also possible to specify multiple getters in a single request, creating identical payments
   * for each getter; such a request is called a "payment pack", which is used, for example, to
   * split a bill amongst a group of customers.
   */
  payment_getters?: UiCustomer[];
  /** The direction of funds for each payment. */
  orientation?: Orientation;
  /** The amount to assign to each payment. */
  amount?: Money;
  /** Free-form text field, populated by the initiator. */
  note?: string;
  /** Synchronously advance payment through passcode confirmation using this token. */
  passcode_token?: string;
  /**
   * Deprecated: sending cheques was never fully implemented.
   *
   * @deprecated
   */
  check_sending_mode?: InitiatePaymentRequestCheckSendingMode;
  /**
   * True if this payment was initiated from an Apple Watch.
   *
   * @deprecated
   */
  sent_via_watch?: boolean;
  /** The instrument selection which includes the accepted fee. */
  instrument_selection?: InstrumentSelection;
  /**
   * True if this payment was initiated from Siri. Deprecated in favor of AppCreationActivity.
   *
   * @deprecated
   */
  sent_via_siri?: boolean;
  /**
   * The unique identifier of the iMessage recipient. The presence of this field indicates that this
   * is a claimable payment sent via iMessage. If present, payment_getters must be empty. Implies
   * is_claimable=true.
   */
  imessage_uuid?: string;
  /** Specifies the giftwrap style of the iMessage payment. */
  imessage_giftwrap?: InitiatePaymentRequestIMessageGiftwrap;
  /** Only useful for the web for hinting the creation mechanism (i.e. either WEB or CASHBOARD). */
  creation_mechanism?: string;
  /**
   * True if the payment is to be sent via iMessage, QR code, or similar. If true, payment_getters
   * must be empty.
   */
  is_claimable?: boolean;
  /**
   * Enables "offline payment cancelation". Because payment requests sometimes fail to complete,
   * apps will retry those requests, which is referred to as "offline payments". cancel_payments
   * allows initiators to attempt to cancel offline payments that may or may not have been received
   * by the server. If present, request_context.retry_context must be present. Apps should prefer
   * to use CancelPaymentRequest when they know the payment token of the payment.
   */
  cancel_payments_data?: InitiatePaymentRequestCancelPaymentsData;
  schedule?: ScheduleRFC2445;
  /**
   * A free form string, typically the name of the app or website that launched the Cash App.
   * Ex: https://twitter.com or "MyQrScannerApp"
   */
  referrer?: string;
  /**
   * The Deep Link URL that launched the Cash App which lead to this payment. Ex: QR Code Deep Link.
   * This is most likely a `https://cash.me` or `https://cash.app` domain name,
   * followed by complete path, including any query parameters.
   */
  launch_url?: string;
  /**
   * The originating in-app activity that lead to the payment being created. Mostly used for
   * tracking and analytics, different from the CreationMechanism above,
   * which is used for more business logic related decisions.
   */
  app_creation_activity?: AppCreationActivity;
  /** Data required when doing an invest payment. */
  invest_payment_data?: InvestPaymentData | undefined;
  gift_card_payment_data?:
    | GiftCardPaymentData
    | undefined;
  /**
   * If this payment was initiated through the profile directory suggestion flow, this id
   * identifies the suggestion which is linked to this payment. Otherwise, null.
   */
  suggestion_id?: string;
  /**
   * The exchange rates associated with this request.
   * Only applicable for multi currency payments.
   */
  exchange_rates_token?: string;
  /**
   * Optional paramater that should only be used when sending recurring payments. When provided,
   * this endpoint skips client authentication.
   * CustomerToken of the payment sender.
   */
  sender_customer_token?: string;
}

/** Deprecated: sending cheques was never fully implemented. */
export enum InitiatePaymentRequestCheckSendingMode {
  NONE = "NONE",
  STANDARD = "STANDARD",
  EXPEDITED = "EXPEDITED",
}

/** Specifies the giftwrap style of the iMessage payment. */
export enum InitiatePaymentRequestIMessageGiftwrap {
  NO_GIFTWRAP = "NO_GIFTWRAP",
  CASH = "CASH",
}

/** Describes the payments that should be canceled (see cancel_payments). */
export interface InitiatePaymentRequestCancelPaymentsData {
  /**
   * Set to true to cancel all payments, as identified by external_id. This includes any claimable
   * payments. If true, cancel_for_getters must be empty.
   */
  cancel_for_all?: boolean;
  /**
   * Identifies specific payments that should be canceled, as identified by external_id and
   * getter. Must be a subset of payment_getters.
   */
  cancel_for_getters?: UiCustomer[];
}

export interface InitiatePaymentResponse {
  response_context?: ResponseContext;
  /**
   * The status of the overall request. If SUCCESS, individual payments may still have failed:
   * see response_context.payments[i].state for the status of each payment.
   */
  status?: InitiatePaymentResponseStatus;
  /** If this is a claimable payment, contains data needed to provide the getter with a claim link. */
  claim_data?: ClaimData;
}

export enum InitiatePaymentResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

export interface ClaimData {
  /** Contains claim data corresponding to the initiated payment. */
  claimable_payment?: ClaimablePayment;
}

export interface ClaimablePayment {
  /** The payment token that the initiator received corresponding to a UiPayment. */
  payment_token?: string;
  /** The token to send to the getter, allowing them to claim the payment. */
  claim_token?: string;
  /** The URL for the getter to claim the payment. */
  claim_url?: string;
}

/**
 * Request OCR of card image. A follow-up call to LinkCardRequest is needed to get PAN.
 * The protocol is described in http://goo.gl/UNdYK5.
 */
export interface OcrCardRequest {
  /** unique ID generated by client. */
  ocr_context?: string;
  image_bytes?: Uint8Array;
  mime_type?: string;
  image_number?: number;
}

export interface OcrCardResponse {
  status?: OcrCardResponseStatus;
}

export enum OcrCardResponseStatus {
  INVALID = "INVALID",
  SUCCESSFULLY_ENQUEUED = "SUCCESSFULLY_ENQUEUED",
  /** ERROR - unspecified error such as server failure. */
  ERROR = "ERROR",
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
}

/** The request to link a debit card. */
export interface LinkCardRequest {
  request_context?: RequestContext;
  /** When ocr_context is not null, `card` may contain only partial info. */
  card?: KeyedCard;
  /** If card linking was initiated by a specific payment or payments, include the payment tokens. */
  payment_tokens?: string[];
  /** If card linking was done using OCR, the PAN info of the keyed card will not be present. */
  ocr_context?: string;
  /**
   * Indicates the type of card that the customer is linking (Debit or credit). If the
   * card ends up not being of the chosen type, the link will fail and the customer will
   * be asked if they want to link the card as the appropriate type, confirming the fees
   * associated with that card. If not provided, the default is DEBIT_CARD.
   */
  instrument_type?: CashInstrumentType;
  /** The transfer being acted upon. */
  transfer_token?: string;
  /** True if the card PAN was scanned using NFC and the PAN has not been modified by the user. */
  linked_via_nfc?: boolean;
  /** Name of the UI experiment treatment used when collecting card information from the customer * */
  treatment?: string;
}

export interface LinkCardResponse {
  response_context?: ResponseContext;
  status?: LinkCardResponseStatus;
  /** Can be null if the failure is not related to a specific field, or related to multiple fields. */
  failure_field?: FieldName;
  /**
   * A token that can be supplied when initiating a payment to skip passcode confirmation for
   * that payment.
   */
  passcode_token?: string;
  /** This will contain the successfully recognized PAN only if linked using OCR. */
  unencrypted_pan_ocr_result?: string;
  /** The instrument that was linked. Only populated on successful links. */
  instrument?: Instrument;
  /**
   * The instrument to use when prompting the customer for passcode verification. Returning it here
   * saves the app from having to call GetProfile immediately after unlinking an instrument.
   */
  verification_instrument_token?: string;
}

export enum LinkCardResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /** FAILURE - The card could not be linked. */
  FAILURE = "FAILURE",
  /**
   * INSTRUMENT_TYPE_MISMATCH - The user attempted to link a credit card as a debit card or vice versa.
   * Only supported in v2.9+.
   */
  INSTRUMENT_TYPE_MISMATCH = "INSTRUMENT_TYPE_MISMATCH",
}

/** The request to unlink a debit card. */
export interface UnlinkCardRequest {
}

export interface UnlinkCardResponse {
  status?: UnlinkCardResponseStatus;
}

export enum UnlinkCardResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** The request to link a bank account. */
export interface LinkBankAccountRequest {
  request_context?: RequestContext;
  /** A 9 digit routing number. */
  routing_number?: string;
  /**
   * 1 or more digits identifying the bank account number. Note that bank account numbers are only
   * loosely validated and it is the caller's responsibility to prevent mistyped account numbers.
   * Applications accepting account numbers in text fields should require the customer to double-key
   * the account number to prevent typos, which can prevent funds from being lost.
   */
  account_number?: string;
  /** If linking was initiated by a specific payment or payments, include the payment tokens. */
  payment_tokens?: string[];
  /** The transfer being acted upon. */
  transfer_token?: string;
}

export interface LinkBankAccountResponse {
  response_context?: ResponseContext;
  /** The bank account for use in the customer's profile. Deprecated in favor of instrument. */
  bank_account?: BankAccount;
  /** The instrument that was linked. Only populated on successful links. */
  instrument?: Instrument;
}

/** The request to unlink a bank account. */
export interface UnlinkBankAccountRequest {
}

export interface UnlinkBankAccountResponse {
}

/**
 * The request to confirm a payment from the Cash app with a passcode. For now and the foreseeable
 * future, the passcode will be a 3-digit CVV. This request can cause the payment to advance, fail
 * or stay at its current state.
 */
export interface ConfirmPasscodeRequest {
  request_context?: RequestContext;
  passcode?: string;
  /** If multiple are provided, they must be from the same payment pack. */
  payment_tokens?: string[];
  /** A token in lieu of a passcode. Used for biometric authentication mechanisms. */
  passcode_token?: string;
  /** The instrument selection which includes the accepted fee. */
  instrument_selection?: InstrumentSelection;
  /** The transfer being acted upon. */
  transfer_token?: string;
}

export interface ConfirmPasscodeResponse {
  response_context?: ResponseContext;
  status?: ConfirmPasscodeResponseStatus;
  /**
   * A token that can be supplied when initiating a payment to skip passcode confirmation for
   * that payment.
   */
  passcode_token?: string;
}

export enum ConfirmPasscodeResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
  INVALID_PASSCODE = "INVALID_PASSCODE",
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
  CARD_BLOCKED = "CARD_BLOCKED",
  FAILURE = "FAILURE",
}

/**
 * The request to confirm a payment from the Cash app. This applies to payments that are
 * suspected duplicates or potentially spoofed, and requests waiting on the bill getter.
 */
export interface ConfirmPaymentRequest {
  request_context?: RequestContext;
  /**
   * Replaced with payment_tokens.
   *
   * @deprecated
   */
  payment_token?: string;
  payment_tokens?: string[];
  /** The instrument selection which includes the accepted fee. */
  instrument_selection?: InstrumentSelection;
}

export interface ConfirmPaymentResponse {
  response_context?: ResponseContext;
  status?: ConfirmPaymentResponseStatus;
  /**
   * The updated payment. Returned for old apps that use the payment_token field.
   *
   * @deprecated
   */
  payment?: UiPayment;
}

export enum ConfirmPaymentResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
  FAILED = "FAILED",
}

/** Request to cancel a payment from the Cash app. */
export interface CancelPaymentRequest {
  request_context?: RequestContext;
  payment_token?: string;
}

export interface CancelPaymentResponse {
  response_context?: ResponseContext;
  status?: CancelPaymentResponseStatus;
  /** The updated payment. */
  payment?: UiPayment;
}

export enum CancelPaymentResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
}

/** Request to refund a payment. */
export interface RefundPaymentRequest {
  request_context?: RequestContext;
  payment_token?: string;
}

export interface RefundPaymentResponse {
  response_context?: ResponseContext;
  /** The updated payment. */
  payment?: UiPayment;
}

/** Request to add a reaction to a Cash entity. */
export interface AddReactionRequest {
  request_context?: RequestContext;
  reaction?: Reaction;
}

/** Response from adding a reaction to a Cash entity. */
export interface AddReactionResponse {
  response_context?: ResponseContext;
}

/** Request to add a new threaded reply to a payment */
export interface AddThreadedPaymentReplyRequest {
  request_context?: RequestContext;
  reply?: ThreadedPaymentReply;
}

/** Response from adding a new threaded reply to a payment */
export interface AddThreadedPaymentReplyResponse {
  response_context?: ResponseContext;
}

/** The request to verify a user's identity with their legal name, birthdate and SSN last four. */
export interface VerifyIdentityRequest {
  request_context?: RequestContext;
  /** The user's legal name. */
  name?: string;
  /** Birthdate as "mm/dd/yyyy". */
  birth_date?: string;
  /** The last four digits in the customer's SSN. */
  ssn_last_four?: string;
  /** The customer's full social security number. */
  ssn?: string;
  /** The customer's address. */
  address?: GlobalAddress;
  payment_tokens?: string[];
  /** The transfer being acted upon. */
  transfer_token?: string;
}

/** The response to a request to verify a user's identity. */
export interface VerifyIdentityResponse {
  response_context?: ResponseContext;
  status?: VerifyIdentityResponseStatus;
  /**
   * If set, this message should be displayed in a dialog in the apps.
   * Deprecated in v2.18 in favor of ResponseContext.
   *
   * @deprecated
   */
  message?: string;
}

export enum VerifyIdentityResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Identity verification succeeded. */
  SUCCESS = "SUCCESS",
  /** CONCURRENT_MODIFICATION - The payment was no longer waiting on this user. */
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
  /**
   * INVALID_IDENTITY - The combination of the user's name, birthdate and last four of SSN are invalid. The
   * user can retry.
   */
  INVALID_IDENTITY = "INVALID_IDENTITY",
  /**
   * FAILURE - The combination of the customer's name, birthdate and last four of SSN are invalid. The
   * user is no longer allowed to try to verify their identity. Any in-flight payments
   * have been canceled.
   */
  FAILURE = "FAILURE",
}

/** Request to get the profile of the customer. */
export interface GetProfileRequest {
}

/** Response to a request to get the profile of the customer. */
export interface GetProfileResponse {
  status?: GetProfileResponseStatus;
  profile?: Profile;
  profile_token?: string;
}

export enum GetProfileResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Request to get the list of conversations in a customer's activity feed. */
export interface GetConversationListRequest {
}

/** Response to a request to get a list of conversations for a customer. */
export interface GetConversationListResponse {
  status?: GetConversationListResponseStatus;
  conversations?: Conversation[];
}

export enum GetConversationListResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Request to set a customer's full name. */
export interface SetFullNameRequest {
  request_context?: RequestContext;
  full_name?: string;
  /** The payments being acted upon. */
  payment_tokens?: string[];
  /** The transfer being acted upon. */
  transfer_token?: string;
}

/** Response to a request to set a customer's full name. */
export interface SetFullNameResponse {
  response_context?: ResponseContext;
  status?: SetFullNameResponseStatus;
}

export enum SetFullNameResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  INVALID_NAME = "INVALID_NAME",
}

/** Set nearby preferences for the customer. */
export interface SetNearbyPrivacyPreferenceRequest {
  nearby_visibility?: NearbyVisibility;
}

export interface SetNearbyPrivacyPreferenceResponse {
  status?: SetNearbyPrivacyPreferenceResponseStatus;
}

export enum SetNearbyPrivacyPreferenceResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

export interface SetRequirePasscodeConfirmationRequest {
  request_context?: RequestContext;
  require_passcode_confirmation?: boolean;
  passcode?: string;
  passcode_token?: string;
}

export interface SetRequirePasscodeConfirmationResponse {
  response_context?: ResponseContext;
  status?: SetRequirePasscodeConfirmationResponseStatus;
  /**
   * A token that can be supplied when initiating a payment to skip passcode confirmation for
   * that payment.
   */
  passcode_token?: string;
}

export enum SetRequirePasscodeConfirmationResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /** INVALID_PASSCODE - The passcode is invalid. The user should retry with the correct passcode. */
  INVALID_PASSCODE = "INVALID_PASSCODE",
  /**
   * TOO_MANY_ATTEMPTS - The passcode is invalid and the user has exhausted the number of attempts to provide it.
   * The user cannot attempt again for 24 hours.
   */
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
  /** CARD_BLOCKED - The card has been blocked. The user must link a new card before changing this setting. */
  CARD_BLOCKED = "CARD_BLOCKED",
  /** OP_NOT_PERMITTED - The operation is not permitted for the given user (e.g. removing passcode in SCA region). */
  OP_NOT_PERMITTED = "OP_NOT_PERMITTED",
}

/** Send an SMS with a link to download the Cash App. */
export interface SendDownloadLinkRequest {
  /** The payments being acted upon. */
  payment_token?: string;
  /** SMS number to send a download link to. */
  sms_number?: string;
  /**
   * Campaign ID to use in download URL. This is a largely free form token,
   * that will have some basic constraints on allowed characters.
   */
  campaign_id?: string;
}

export interface SendDownloadLinkResponse {
  status?: SendDownloadLinkResponseStatus;
}

export enum SendDownloadLinkResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Request was successful, SMS was sent. */
  SUCCESS = "SUCCESS",
  INVALID_SMS_NUMBER = "INVALID_SMS_NUMBER",
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
}

/** The request to register an email address as the first step to claiming it. */
export interface RegisterEmailRequest {
  request_context?: RequestContext;
  email_address?: string;
  /**
   * Deprecated. Use client scenario header.
   *
   * @deprecated
   */
  scenario?: RegisterEmailRequestScenario;
  /** The payments being acted upon. */
  payment_tokens?: string[];
  /** If true, this request will fail if a new customer must be created for this alias to be claimed. */
  existing_customer_only?: boolean;
  /**
   * Optional. If provided, this oauth enrollment token will be validated and used to customize the
   * challenge login code email that is sent. It is created during an OAuth enrollment flow.
   */
  enrollment_token?: string;
  device_location_heuristics?: DeviceLocationHeuristics;
  /** Tags are out of order. Next tag = 11. */
  account_id?: string;
}

export enum RegisterEmailRequestScenario {
  /** ONBOARDING - The customer is registering their email as part of onboarding. */
  ONBOARDING = "ONBOARDING",
  /** PROFILE - The customer is registering their email from their profile. */
  PROFILE = "PROFILE",
}

export interface RegisterEmailResponse {
  response_context?: ResponseContext;
  status?: RegisterEmailResponseStatus;
}

export enum RegisterEmailResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The request was successful, an email has been sent. */
  SUCCESS = "SUCCESS",
  /** INVALID_EMAIL_ADDRESS - The email address did not conform to an acceptable format. */
  INVALID_EMAIL_ADDRESS = "INVALID_EMAIL_ADDRESS",
  /**
   * TOO_MANY_REQUESTS - There have been too many requests to register this email address in the last 24 hours.
   * The user can try again later.
   */
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
  /**
   * DUPLICATE_EMAIL_ADDRESS - The email address is already associated with the requesting user.
   * Added for 2.11
   */
  DUPLICATE_EMAIL_ADDRESS = "DUPLICATE_EMAIL_ADDRESS",
  /** CUSTOMER_DOES_NOT_EXIST - No customer exists for this email address, and existing_customer_only was set in the request. */
  CUSTOMER_DOES_NOT_EXIST = "CUSTOMER_DOES_NOT_EXIST",
  /** INVALID_ENROLLMENT_TOKEN - The enrollment_token did not exist, or was no longer valid. */
  INVALID_ENROLLMENT_TOKEN = "INVALID_ENROLLMENT_TOKEN",
}

/** The request to verify an email address with a verification code. */
export interface VerifyEmailRequest {
  request_context?: RequestContext;
  email_address?: string;
  verification_code?: string;
  /** The payments being acted upon. */
  payment_tokens?: string[];
  /**
   * The clients security / privacy context information. This data is currently used in client
   * app-level encryption and hardware attestation. Provide this when session is to be minted
   * during verifyEmail.
   */
  client_security_context?: ClientSecurityContext;
  /** Tags are out of order. Next tag = 10. */
  account_id?: string;
}

/** The response to a request to verify an email address. On success it contains the session token. */
export interface VerifyEmailResponse {
  response_context?: ResponseContext;
  status?: VerifyEmailResponseStatus;
  /** @deprecated */
  session_token?: string;
  /**
   * No longer used.
   *
   * @deprecated
   */
  customer_verification_data?: CustomerVerificationData;
}

export enum VerifyEmailResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The request was successful. A session token has been provided. */
  SUCCESS = "SUCCESS",
  /**
   * INVALID_EMAIL_ADDRESS - The email address provided did not conform to an acceptable format or has not been
   * previously registered.
   */
  INVALID_EMAIL_ADDRESS = "INVALID_EMAIL_ADDRESS",
  /** INVALID_VERIFICATION_CODE - The verification code is invalid. The user should retry with the correct verification code. */
  INVALID_VERIFICATION_CODE = "INVALID_VERIFICATION_CODE",
  /**
   * EXPIRED_VERIFICATION_CODE - The verification code has expired. The user should register their email address again to get
   *  a new verification code.
   */
  EXPIRED_VERIFICATION_CODE = "EXPIRED_VERIFICATION_CODE",
  /**
   * TOO_MANY_FAILED_ATTEMPTS - There have been too many failed attempts to verify this email address in the past 7 days.
   * The user can try again later.
   */
  TOO_MANY_FAILED_ATTEMPTS = "TOO_MANY_FAILED_ATTEMPTS",
  /** SUCCESS_WITH_VERIFICATION_DATA - No longer used. */
  SUCCESS_WITH_VERIFICATION_DATA = "SUCCESS_WITH_VERIFICATION_DATA",
  /** NOT_ELIGIBLE - Only for 2.5+ apps: this action was related to a customer merge that was deemed not eligible. */
  NOT_ELIGIBLE = "NOT_ELIGIBLE",
}

/** The request to unregister (unlink) an email address or SMS number. */
export interface UnregisterAliasRequest {
  request_context?: RequestContext;
  alias?: UiAlias;
}

export interface UnregisterAliasResponse {
  response_context?: ResponseContext;
  status?: UnregisterAliasResponseStatus;
}

export enum UnregisterAliasResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/**
 * The request to get the configuration for sending invitations.
 *
 * Note: This data can now be fetched in GetAppConfigRequest instead. Going forward, the app
 * should use it instead of GetInvitationConfigRequest.
 */
export interface GetInvitationConfigRequest {
  /** The list of invitation treatments that this app supports. */
  supported_treatments?: InvitationConfigInvitationTreatment[];
}

/** The response to a request to get the configuration for sending invitations. */
export interface GetInvitationConfigResponse {
  status?: GetInvitationConfigResponseStatus;
  /** The SMS message text that should be used when sending the invitations. */
  message?: string;
  /** The amount that the customer will get as a bounty for each invited contact that joins Cash. */
  bounty_amount?: Money;
  /**
   * True if the app should allow the customer to invite contacts. If false, the app should skip
   * inviting contacts.
   */
  enabled?: boolean;
  /** Variants in an A/B/C experiment to improve the invitation flow. */
  invitation_treatment?: InvitationConfigInvitationTreatment;
  /** The amount that the referee will get as a welcome bonus after joining Cash. */
  welcome_bonus_amount?: Money;
  /** The header text to be shown to the user. */
  header_text?: string;
}

export enum GetInvitationConfigResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** The request to register invitations that were sent by a customer. */
export interface RegisterInvitationsRequest {
  /**
   * Deprecated in favor of enhanced_aliases
   * The list of hashed SMS numbers that were sent invitations or that represent additional
   * SMS numbers of contacts that were previously invited.
   *
   * @deprecated
   */
  hashed_sms_numbers?: string[];
  /**
   * Deprecated in favor of enhanced_aliases
   * Invitation treatment used to generate this invitation.
   *
   * @deprecated
   */
  invitation_treatment?: InvitationConfigInvitationTreatment;
  /**
   * Deprecated in favor of enhanced_aliases
   * The list of additional email addresses of contacts that were previously invited.
   *
   * @deprecated
   */
  hashed_email_addresses?: string[];
  /**
   * Deprecated
   * Set to true if the customer modified the invitation message in the preview dialog.
   *
   * @deprecated
   */
  invitation_message_modified?: boolean;
  /**
   * A list of invited aliases.
   * All new RegisterInvitationsRequest should populate this field. hashed_sms_numbers,
   * hashed_email_addresses and invitation_treatment will be ignored if enhanced_aliases exist.
   */
  enhanced_aliases?: RegisterInvitationsRequestEnhancedAlias[];
}

export interface RegisterInvitationsRequestEnhancedAlias {
  type?: RegisterInvitationsRequestEnhancedAliasType;
  hashed_alias?: string;
  unhashed_alias?: string;
  invitation_treatment?: InvitationConfigInvitationTreatment;
}

export enum RegisterInvitationsRequestEnhancedAliasType {
  SMS = "SMS",
  EMAIL = "EMAIL",
}

/** The response to a request to register invitations that were sent by a customer. */
export interface RegisterInvitationsResponse {
  status?: RegisterInvitationsResponseStatus;
  /** A list of invitation tokens */
  invitation_tokens?: string[];
}

export enum RegisterInvitationsResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** The request to send email invitations on behalf of a customer. */
export interface InviteContactsRequest {
  request_context?: RequestContext;
  email_addresses?: string[];
}

export interface InviteContactsResponse {
  response_context?: ResponseContext;
  /**
   * For any invitations that could not be sent, the email address will be included in
   * unsent_email_addresses so that the app doesn't mark them as invited.
   */
  unsent_email_addresses?: string[];
}

/** A request that indicates completing the InviteFriendsBlocker. */
export interface CompleteInviteFriendsRequest {
  request_context?: RequestContext;
}

export interface CompleteInviteFriendsResponse {
  response_context?: ResponseContext;
}

/**
 * Get my token to broadcast encrypted. To the app, its an opaque token. For now, its actually
 * just the app token.
 */
export interface GetNearbyTokenRequest {
}

export interface GetNearbyTokenResponse {
  token?: string;
}

/**
 * Get public information about a Platform Application Client (like SnapCash).
 * This is used during OAuth flows to show the customer info about the app they're authorizing.
 * Only one of `platform_client_id` and `enrollment_token` can be provided.
 */
export interface GetPlatformClientRequest {
  platform_client_id?: string;
  enrollment_token?: string;
}

export interface GetPlatformClientResponse {
  status?: GetPlatformClientResponseStatus;
  platform_client?: AccountIntegration;
}

export enum GetPlatformClientResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
}

export interface GetActiveIntegrationsRequest {
  request_context?: RequestContext;
}

export interface GetActiveIntegrationsResponse {
  response_context?: ResponseContext;
  account_integrations?: AccountIntegration[];
}

export interface AccountIntegration {
  /** Client display name. */
  title?: string;
  /** Url to client icon. */
  icon_url?: string;
  /** List of permissions. */
  scopes?: AccountIntegrationScope[];
  /**
   * The client id (Deprecated, use client_id instead).
   *
   * @deprecated
   */
  id?: string;
  /** A client specifiable identifier for the integration. */
  customer_descriptor?: string;
  /** The client id. */
  client_id?: string;
  /** The external client visible identifier. */
  external_id?: string;
}

export interface AccountIntegrationScope {
  title?: string;
  description?: string;
}

/**
 * Approve a Platform Client (Oauth), associating it with the current customer's account.
 * Only one of `enrollment_token` or `platform_client_id` can be provided.
 * If a enrollment_token is provided we use that to lookup the Platform Client and Dummy Customer.
 */
export interface ApprovePlatformClientRequest {
  enrollment_token?: string;
  /** response_type can be "token" or "code" */
  response_type?: string;
  scope?: string;
  redirect_uri?: string;
}

export interface ApprovePlatformClientResponse {
  status?: ApprovePlatformClientResponseStatus;
  code?: string;
  access_token?: string;
  expires_at?: string;
  token_type?: string;
}

export enum ApprovePlatformClientResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
}

/**
 * Look up a customer by encrypted token. This is meant to look up BLE tokens. The response
 * contains the franklin customer tokens for the customer-merge closure of the request.
 */
export interface LookupCustomerRequest {
  encrypted_token?: Uint8Array;
}

export interface LookupCustomerResponse {
  status?: LookupCustomerResponseStatus;
  customer?: UiCustomer;
}

export enum LookupCustomerResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /** NOT_FOUND - Customer not found. */
  NOT_FOUND = "NOT_FOUND",
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
}

export interface SetProfilePhotoRequest {
  image_data?: string;
  image_bytes?: Uint8Array;
  /** Tags are out of order. Next tag = 6. */
  mime_type?: string;
}

export interface SetProfilePhotoResponse {
  status?: SetProfilePhotoResponseStatus;
  image_url?: string;
}

export enum SetProfilePhotoResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Tags are out of order. Next tag = 3. */
export interface ClearProfilePhotoRequest {
}

export interface ClearProfilePhotoResponse {
  status?: ClearProfilePhotoResponseStatus;
}

export enum ClearProfilePhotoResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Request to register a device for push notifications. */
export interface RegisterDeviceRequest {
  device_token?: string;
  /**
   * Only used for iOS devices. Required so that our server can use the right certs when it
   * connects to the APNS service.
   */
  build_type?: BuildType;
  /**
   * iOS can register pushes as silent-only, in which case we can't send user visible
   * notifications to this device.
   */
  can_receive_user_visible_notifications?: boolean;
}

/** Response to a request to register a device for push notifications. */
export interface RegisterDeviceResponse {
  status?: RegisterDeviceResponseStatus;
}

export enum RegisterDeviceResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Request an app sign out. */
export interface SignOutRequest {
}

export interface SignOutResponse {
  status?: SignOutResponseStatus;
}

export enum SignOutResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Request to reset a customer's badge count. */
export interface ResetBadgeRequest {
  /**
   * If a conversation_token is specified, then the badging associated with only that conversation
   * will be reset. If all attributes are omitted, all badges will be reset.
   */
  conversation_token?: string;
  /**
   * An optional array of payment tokens to reset badges. If all attributes are omitted,
   * all badges are reset.
   */
  payment_tokens?: string[];
  /**
   * An optional array of sync entity IDs whose badges the client wants reset.
   * If this is not specified, it suffices to reset all badges for the customer.
   */
  entity_ids?: string[];
}

export interface ResetBadgeResponse {
  status?: ResetBadgeResponseStatus;
}

export enum ResetBadgeResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Request to trigger the creation of a welcome bonus payment for this customer if they qualify. */
export interface TriggerWelcomeBonusRequest {
}

/** Response to a request to trigger a welcome bonus. */
export interface TriggerWelcomeBonusResponse {
  status?: TriggerWelcomeBonusResponseStatus;
}

export enum TriggerWelcomeBonusResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /** NOT_QUALIFIED - The customer is not qualified to receive a welcome bonus. */
  NOT_QUALIFIED = "NOT_QUALIFIED",
}

/**
 * Support claiming a previously unclaimed SMS by passing a payment token for an in-flight,
 * unclaimed payment back to the server from an SMS provided web link.
 */
export interface ClaimByPaymentTokenRequest {
  request_context?: RequestContext;
  payment_token?: string;
  instrument_selection?: InstrumentSelection;
}

/**
 * Response to attempt to claim SMS from a payment token. If the claim is successful, the
 * SMS is claimed and an app_token and session_token are returned to the client.
 */
export interface ClaimByPaymentTokenResponse {
  response_context?: ResponseContext;
  status?: ClaimByPaymentTokenResponseStatus;
  /**
   * ScenarioPlan contains info about additional screens to show the user to complete the scenario.
   * Deprecated in v2.18 in favor of ResponseContext.
   *
   * @deprecated
   */
  scenario_plan?: ScenarioPlan;
}

export enum ClaimByPaymentTokenResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  ALREADY_CLAIMED = "ALREADY_CLAIMED",
  FAILED = "FAILED",
}

/** Lookup a single payment by sender or recipient token. */
export interface GetPaymentRequest {
  request_context?: RequestContext;
  payment_token?: string;
}

export interface GetPaymentResponse {
  response_context?: ResponseContext;
  payment?: UiPayment;
  /**
   * A list of the customer's currently linked instruments. At present, it will contain
   * at most one debit card, at most one credit card, at most one bank account, and
   * at most one Cash balance.
   */
  instruments?: Instrument[];
  /**
   * A list of the customer's pending instruments. At present, it will contain
   * at most one debit card, and at most one credit card.
   */
  pending_instruments?: Instrument[];
}

/** Lookup a single payment by claim token. */
export interface GetClaimablePaymentRequest {
  request_context?: RequestContext;
  payment_token?: string;
}

export interface GetClaimablePaymentResponse {
  response_context?: ResponseContext;
  payment?: UiPayment;
  claim_status?: ClaimStatus;
  /**
   * A URL used for claimable payments, leads back to the Client app when payment is declined or
   * already claimed.
   */
  redirect_url?: string;
  recipient?: UiCustomer;
}

/** Lookup a single transaction by transaction token. */
export interface GetTransactionRequest {
  request_context?: RequestContext;
  transaction_token?: string;
}

export interface GetTransactionResponse {
  response_context?: ResponseContext;
  payment?: UiPayment;
  /**
   * A list of the customer's currently linked instruments. At present, it will contain
   * at most one debit card, at most one credit card, at most one bank account, and
   * at most one Cash balance.
   */
  instruments?: Instrument[];
}

/**
 * Request to check the app's version. Old apps will be prompted to upgrade. Really old apps
 * will be forced to upgrade or quit. The definition of old and really old is configurable
 * on the server.
 *
 * <p> This request is intended to be made on app startup. The app doesn't need to provide
 * authentication. Its response is strictly based on the app's version in the user agent.
 */
export interface CheckVersionRequest {
}

/** Response to a request to check the app's version. */
export interface CheckVersionResponse {
  status?: CheckVersionResponseStatus;
  /** The message to display in the dialog. */
  message?: string;
  /** The title of the dialog. */
  title?: string;
}

export enum CheckVersionResponseStatus {
  INVALID = "INVALID",
  /** VERSION_OK - The app's version is supported. */
  VERSION_OK = "VERSION_OK",
  /**
   * UPGRADE_SUGGESTED - The app's version is old and we'd like to upgrade these customers to the current version.
   * When the app receives this response, it should display a dialog indicating that the
   * customer should update to the latest version. The primary button should go to the
   * App/Play Store. The secondary button should close the dialog.
   */
  UPGRADE_SUGGESTED = "UPGRADE_SUGGESTED",
  /**
   * UPGRADE_REQUIRED - The app's version is no longer supported. The customer must upgrade to the current version.
   * When the app receives this response, it should display a dialog indicating that the
   * customer must update to the latest version. The primary button should go to the
   * App/Play Store. The secondary button should exit the app. This should be used sparingly.
   */
  UPGRADE_REQUIRED = "UPGRADE_REQUIRED",
}

/** The request to get the public cashtag profile information. */
export interface GetCashtagProfileRequest {
  /** The unprefixed cashtag (no leading currency symbol) */
  cashtag?: string;
}

export interface GetCashtagProfileResponse {
  status?: GetCashtagProfileResponseStatus;
  /** The public profile, if it exists and is enabled. */
  profile?: CashCustomerProfile;
}

export enum GetCashtagProfileResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  NOT_PUBLIC = "NOT_PUBLIC",
}

/** The request to get the status and availability of a cashtag. */
export interface GetCashtagStatusRequest {
  cashtag_candidate?: string;
}

export interface GetCashtagStatusResponse {
  status?: GetCashtagStatusResponseStatus;
  /** The same value as passed in the request. */
  cashtag_candidate?: string;
  /** The status of the requested cashtag. */
  cashtag_status?: CashtagStatus;
  /** The text to display for the cashtag_url. e.g. "cash.me/$noble" */
  cashtag_url_display_text?: string;
  /** The failure_message will be populated when the cashtag_status is not AVAILABLE. */
  failure_message?: string;
  /**
   * When non-null, the app should present a dialog with the confirm_message before allowing
   * the user to select this cashtag.
   * e.g. "Are you sure you want to change your Cashtag? This is the last time you can do this."
   */
  confirm_message?: string;
}

export enum GetCashtagStatusResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  TOO_MANY_REQUESTS = "TOO_MANY_REQUESTS",
}

/** The request to set the cashtag for a customer. */
export interface SetCashtagRequest {
  request_context?: RequestContext;
  cashtag?: string;
}

export interface SetCashtagResponse {
  response_context?: ResponseContext;
  status?: SetCashtagResponseStatus;
  /** The normalized URL e.g. "https://cash.me/$noble" */
  cashtag_url?: string;
  /** The text to display for the cashtag_url. e.g. "cash.me/$noble" */
  cashtag_url_display_text?: string;
  /**
   * If the response status is CASHTAG_UNAVAILABLE, this field will be populated with the status
   * of the cashtag.
   */
  cashtag_status?: CashtagStatus;
}

export enum SetCashtagResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /**
   * CASHTAG_STATUS_FAILURE - The cashtag is invalid or no longer available. Check the cashtag status for details. The
   * failure message contains a message to display to the customer.
   */
  CASHTAG_STATUS_FAILURE = "CASHTAG_STATUS_FAILURE",
  /**
   * FAILURE - The cashtag was available but the request failed for another reason. The failure message
   * contains a message to display to the customer. The failure can be due to the customer not
   * having a debit card, or they may have exceeded a limit on the number of cashtags that can be
   * linked to their customer or debit card.
   */
  FAILURE = "FAILURE",
}

/** The request to get a suggested cashtag for a customer. */
export interface GetCashtagSuggestionRequest {
}

export interface GetCashtagSuggestionResponse {
  status?: GetCashtagSuggestionResponseStatus;
  /** A suggested cashtag. This can be null if there is no good suggestion. */
  cashtag_suggestion?: string;
}

export enum GetCashtagSuggestionResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** The request to enable or disable the public profile page for the customer's cashtag. */
export interface SetCashtagUrlEnabledRequest {
  enabled?: boolean;
  request_context?: RequestContext;
}

export interface SetCashtagUrlEnabledResponse {
  status?: SetCashtagUrlEnabledResponseStatus;
  response_context?: ResponseContext;
}

export enum SetCashtagUrlEnabledResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** The request to update the customers incoming request policy. */
export interface SetIncomingRequestPolicyRequest {
  request_context?: RequestContext;
  incoming_request_policy?: IncomingRequestPolicy;
}

/** The response to updating the customers incoming request policy */
export interface SetIncomingRequestPolicyResponse {
  response_context?: ResponseContext;
  status?: SetIncomingRequestPolicyResponseStatus;
}

export enum SetIncomingRequestPolicyResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

/**
 * The request to find customers using search text. Can be any fully qualified cashtag, email or
 * phone number. The route does not support prefix matching so only exact matches will be returned.
 * If the search text includes a leading currency symbol, than it is assumed to be a cashtag and
 * treated as such. Otherwise, the search text is first compared to a phone number pattern. If it
 * is not a proper phone number than it is compared to an email address pattern. If not a valid
 * email than a the search text is assumed to be a cashtag without a currency prefix.
 */
export interface FindCustomersRequest {
  search_text?: string;
  /**
   * The orientation of the payment for which we are searching customers.
   *
   * @deprecated
   */
  orientation?: Orientation;
}

export interface FindCustomersResponse {
  status?: FindCustomersResponseStatus;
  /** The search text that was used to generate this response. */
  search_text?: string;
  /**
   * The list of customers with the popular cashtags that match the search_text. The matches are
   * ordered with the most popular customer at the beginning of the list.
   */
  matches?: UiCustomer[];
  /** The customer whose cashtag is an exact match of the search_text. Null if none exists. */
  exact_match?: UiCustomer;
  /** Hints to the client how results should be displayed. */
  sort?: FindCustomersResponseSort;
}

export enum FindCustomersResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Hint to clients for preferred sort order of results. */
export enum FindCustomersResponseSort {
  /** EXACT_LOCAL_SERVER - Exact match is displayed first, then local matches, then server suggestions. */
  EXACT_LOCAL_SERVER = "EXACT_LOCAL_SERVER",
  /** LOCAL_EXACT_SERVER - Local matches are displayed first, then exact match, then server suggestions. */
  LOCAL_EXACT_SERVER = "LOCAL_EXACT_SERVER",
}

/** The request to set the rate plan (e.g. business or personal) for a customer. */
export interface SetRatePlanRequest {
  request_context?: RequestContext;
  rate_plan?: RatePlan;
  /**
   * Set to true when the customer is locking in their rate plan. Set to false when the customer
   * is advancing to see the details of a rate plan, but hasn't committed to it yet.
   */
  commit?: boolean;
  payment_tokens?: string[];
  /** The intended_usage from the IntentedUsageItem that was selected. */
  intended_usage?: string;
}

export interface SetRatePlanResponse {
  response_context?: ResponseContext;
  status?: SetRatePlanResponseStatus;
}

export enum SetRatePlanResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /** NOT_ALLOWED - The customer cannot change to the specified rate plan. */
  NOT_ALLOWED = "NOT_ALLOWED",
}

/**
 * The request to get all server-driven configuration for the app, including localized strings
 * and other parameters for various features. The server can change these values from time to time,
 * and thus the app should fetch config when a customer begins or resumes using it.
 */
export interface GetAppConfigRequest {
}

export interface GetAppConfigResponse {
  status?: GetAppConfigResponseStatus;
  invitation_config?: InvitationConfig;
  rate_plan_config?: RatePlanConfig;
  recipient_config?: RecipientConfig;
  ocr_config?: OcrConfig;
  instrument_linking_config?: InstrumentLinkingConfig;
  sharing_config?: SharingConfig;
  apple_watch_config?: AppleWatchConfig;
  support_config?: SupportConfig;
  payment_history_config?: PaymentHistoryConfig;
  institutions_config?: InstitutionsConfig;
  web_login_config?: WebLoginConfig;
  blockers_config?: BlockersConfig;
  offline_config?: OfflineConfig;
  stamps_config?: StampsConfig;
  cryptocurrency_config?: CryptocurrencyConfig;
  cash_drawer_config?: CashDrawerConfig;
  boost_config?: BoostConfig;
  reaction_config?: ReactionConfig;
  scheduled_payments_config?: ScheduledPaymentsConfig;
  banking_config?: BankingConfig;
  check_deposit_config?: CheckDepositConfig;
  treehouse_config?: TreehouseConfig;
  /** a very strict subset of the flags returned from GetFeatureFlagsResponse */
  feature_flags?: FeatureFlag[];
  market_capabilities_config?: MarketCapabilitiesConfig;
  global_config?: GlobalConfig;
}

export enum GetAppConfigResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** Banking configuration */
export interface BankingConfig {
  strings?: BankingConfigStrings;
  recurring_deposits_dda_upsell?: BankingConfigRecurringDepositsDdaUpsell;
}

/** These copies depend on the customer's region (i.e. whether or not DDAs are available to them). */
export interface BankingConfigStrings {
  main_screen_title?: string;
  main_screen_balance_subtitle?: string;
  balance_screen_title?: string;
}

/** Region-dependent (i.e. customers with DDAs available) DDA upsell copy on the Recurring Deposits Screen. */
export interface BankingConfigRecurringDepositsDdaUpsell {
  /** The title, e.g. "Automatically add money to your account with direct deposit." */
  title?: string;
  /** The body, e.g. "Get your paycheck up to 2 days earlier and save on ATM fees." */
  body?: string;
  /** The CTA, e.g. "Set up direct deposit." */
  cta?: string;
}

export interface CheckDepositConfig {
  image?: CheckDepositConfigImage;
  description?: string;
}

export interface CheckDepositConfigImage {
  light_image_url?: string;
  dark_image_url?: string;
}

/** The scheduled payments configuration */
export interface ScheduledPaymentsConfig {
  supported_schedules?: ScheduledPaymentsConfigSupportedSchedule[];
}

/** Schedule frequency/interval pairs, which the server currently supports. */
export interface ScheduledPaymentsConfigSupportedSchedule {
  description?: string;
  schedule?: ScheduleRFC2445;
}

/** The reaction configuration applicable to this app / customer. */
export interface ReactionConfig {
  max_emoji_per_reaction?: number;
  extended_picker_reactions?: Reaction[];
}

/** The request to get the current ScenarioPlan. Use this request when resuming onboarding. */
export interface GetScenarioPlanRequest {
  request_context?: RequestContext;
}

export interface GetScenarioPlanResponse {
  response_context?: ResponseContext;
  status?: GetScenarioPlanResponseStatus;
}

export enum GetScenarioPlanResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Tags are out of order. Next tag = 3. */
  SUCCESS = "SUCCESS",
}

/** The request to complete a client scenario. */
export interface CompleteScenarioRequest {
  request_context?: RequestContext;
  payment_tokens?: string[];
  transfer_token?: string;
}

export interface CompleteScenarioResponse {
  response_context?: ResponseContext;
  status?: CompleteScenarioResponseStatus;
}

export enum CompleteScenarioResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
}

/**
 * The `GetFlow` endpoint starts flows either with server provided data or for a specific Flow.Type.
 * The server provided data is commonly delivered to the mobile client through a deep link URL.
 */
export interface GetFlowRequest {
  request_context?: RequestContext;
  /**
   * Opaque, server-provided data encoded as a string which identifies a flow to present to the
   * customer along with parameters.
   */
  initiation_data?:
    | string
    | undefined;
  /** Start a flow given a specific FlowType. This does not allow parameters. */
  flow_type?: FlowType | undefined;
}

/**
 * Based on the initiation_data provided in the matching request message, the API will provide a
 * response that includes the retrieved blockers flow instance.
 * That flow instance consists of four parts:
 *   - Scenario Plan
 *   - Status Result
 *   - Flow Token
 *   - Client Scenario
 */
export interface GetFlowResponse {
  /**
   * Scenario Plan and Status Result are the primary response data to the request, and are found
   * within the response_context property.
   * Both the Scenario Plan and Status Result are optional. It is not valid to omit both, but it is
   * valid to include both (`scenario_plan` OR `status_result`).
   */
  response_context?: ResponseContext;
  /**
   * A client_scenario associated with the provided blockers flow.
   *
   * The Android client requires a value for this field.
   * For the iOS client this field is optional.
   */
  client_scenario?: ClientScenario;
  /**
   * The flow token for the blockers flow instance. This field is required and non-nullable.
   * This token should be included in all further client requests that resolve blockers for this flow
   * instance.
   */
  token?: string;
}

/** Handles calls at the end of web onboarding that can cause a welcome notification to be sent. */
export interface FlowCompleteRequest {
}

export interface FlowCompleteResponse {
}

export interface SkipBlockerRequest {
  request_context?: RequestContext;
  blockers?: Blockers;
}

export interface SkipBlockerResponse {
  response_context?: ResponseContext;
  status?: SkipBlockerResponseStatus;
}

export enum SkipBlockerResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /**
   * CONCURRENT_MODIFICATION - The blocker provided cannot be skipped, either because it is unavailable, or not
   * possible to skip in this flow.
   */
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
}

/** The request to confirm or decline a merge. */
export interface ResolveMergeRequest {
  request_context?: RequestContext;
  confirm_merge?: boolean;
}

export interface ResolveMergeResponse {
  response_context?: ResponseContext;
  status?: ResolveMergeResponseStatus;
}

export enum ResolveMergeResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

/** DEPRECATED: Get a list of suggested hashed aliases to display as suggested invitations. */
export interface GetSuggestedContactsRequest {
}

/** DEPRECATED */
export interface GetSuggestedContactsResponse {
  /** @deprecated */
  hashed_aliases?: Uint8Array[];
  /**
   * The app will display at most these many contacts. The hashed_aliases list above may contain
   * more than this number of contacts. The reason why we cannot trim the list server side is that
   * the server does not know about different aliases of the same address book contact and cannot
   * definitively filter already invited and Cash customers.
   *
   * @deprecated
   */
  max_number_of_contacts_to_suggest?: number;
}

export interface GetAppMessageRequest {
  message_token?: string;
}

export interface GetAppMessageResponse {
  message_payload?: AppMessagePayload;
}

export interface GetAppMessageTemplatesRequest {
  paging_token?: string;
}

export interface GetAppMessageTemplatesResponse {
  next_paging_token?: string;
  /**
   * List of bulletin app messages in the json format. Json format is used because of not being
   * able to import app message proto due to the circular dependency.
   */
  message_payload_json?: string[];
}

export interface RegisterAppMessageActionRequest {
  message_token?: string;
  /** The identifier of the action taken by the customer upon seeing the app message. */
  action_identifier?: string;
}

export interface RegisterAppMessageActionResponse {
}

export interface SetAppMessagePreferenceRequest {
  notifications_enabled?: boolean;
  request_context?: RequestContext;
}

export interface SetAppMessagePreferenceResponse {
  response_context?: ResponseContext;
}

/** The request to change the customer's preferred way of enjoying display bitcoin amounts. */
export interface SetBitcoinDisplayPreferenceRequest {
  display_units?: BitcoinDisplayUnits;
  request_context?: RequestContext;
}

/** The response to a request to change the customer's preferred way of enjoying display bitcoin amounts. */
export interface SetBitcoinDisplayPreferenceResponse {
  response_context?: ResponseContext;
}

export interface CheckRewardCodeRequest {
  code?: string;
}

export interface CheckRewardCodeResponse {
  valid?: boolean;
  /** A string that may include the name of the inviter, if known and the caller is not rate limited. */
  reward_text?: string;
  /** The inviter's profile photo URL, if available and if the caller is not rate limited. */
  inviter_photo_url?: string;
  /** The inviter's full name, potentially null. */
  inviter_full_name?: string;
  inviter_customer_token?: string;
}

export interface ApplyRewardCodeRequest {
  request_context?: RequestContext;
  code?: string;
}

export interface ApplyRewardCodeResponse {
  response_context?: ResponseContext;
  valid?: boolean;
  /** RewardStatus */
  reward_status?: RewardStatus;
}

export interface GetRewardStatusRequest {
}

export interface GetRewardStatusResponse {
  reward_status?: RewardStatus;
}

export interface GetPaymentRewardStatusRequest {
  payment_tokens?: string[];
}

export interface GetPaymentRewardStatusResponse {
  payment_reward_status?: PaymentRewardStatus;
  /**
   * This may be present to bring the app up to date on reward status sooner than with a separate
   * call. If payment_reward_status requires payment display, this is guaranteed to be provided.
   */
  reward_status?: RewardStatus;
}

/**
 * This proto is intended to be used by web only. Clients that can store sync entities locally
 * are encouraged to use SyncEntitiesRequest instead. Entities are sorted in descending order.
 */
export interface GetPagedSyncEntitiesRequest {
  /** Max number of entries to return. */
  limit?: number;
  /** Pagination token */
  offset?: string;
  begin_time?: string;
  end_time?: string;
  /** Search terms to match: sender, recipient, amount, notes, etc. */
  search_terms?: string;
  /** True to include completed payments. */
  show_completed?: boolean;
  /** True to include incomplete (pending) payments. */
  show_in_flight?: boolean;
  show_failed_transfers?: boolean;
  show_sent?: boolean;
  show_received?: boolean;
  other_customer_id?: string;
  /**
   * Metadata stored on the client that describes all the entities that have been sent to the device
   * (in this an in previous requests). This is opaque to the client and isn't directly interpreted.
   * In a follow-up request, these are echoed back to the server (as received in a
   * previous request) or empty if this is the first sync.
   */
  all_known_ranges?: Uint8Array[];
}

export interface GetPagedSyncEntitiesResponse {
  entities?: SyncEntity[];
  /**
   * When next_request is populated, the server could not fit all requested entities into this
   * response. The client should call back with the next_request to fetch the remaining entities.
   */
  next_request?: GetPagedSyncEntitiesRequest;
  /**
   * A request that can be used to get all future sync entities that haven't happened yet.
   * Client can use this to start getting SyncEntities since the page was loaded.
   * NOTE: This is not dependent on the contents of the request or response. It is the global
   * lastest syncId for this customer.
   */
  updates_request?: SyncEntitiesRequest;
  /**
   * Metadata stored on the client that describes all the entities that have been sent to the device
   * (in this an in previous requests). This is opaque to the client and isn't directly interpreted.
   * In a follow-up request, these are echoed back to the server as-is.
   */
  all_known_ranges?: Uint8Array[];
}

export interface SetAddressRequest {
  request_context?: RequestContext;
  /** The customer's address (which may include just the postal code). */
  postal_address?: GlobalAddress;
  /** List of payment tokens in the current flow. */
  payment_tokens?: string[];
  /** The transfer being acted upon. */
  transfer_token?: string;
  /** True if the address has been validated by (e.g.) Google Places. */
  validated?: boolean;
}

export interface SetAddressResponse {
  response_context?: ResponseContext;
  status?: SetAddressResponseStatus;
  /**
   * A message to display to the customer, typically used when the request failed.
   * Deprecated in v2.18 in favor of ResponseContext.
   *
   * @deprecated
   */
  message?: string;
  /**
   * Echos back the actual address stored by the server. Server might add some address
   * fields e.g. city can be inferred from the postal code provided.
   */
  postal_address?: GlobalAddress;
}

export enum SetAddressResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
  FAILURE = "FAILURE",
}

export interface VerifyCheckAddressRequest {
  postal_address?: PostalAddress;
}

export interface VerifyCheckAddressResponse {
  status?: VerifyCheckAddressResponseStatus;
  /** A canonical rewrite of the address, as supplied by an external provider. */
  canonical_postal_address?: PostalAddress;
  address_correction_message?: string;
}

export enum VerifyCheckAddressResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
}

export interface SetCountryRequest {
  request_context?: RequestContext;
  /** The country selected by the customer. */
  country_code?: Country;
}

export interface SetCountryResponse {
  response_context?: ResponseContext;
  status?: SetCountryResponseStatus;
}

export enum SetCountryResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - Country changed successfully. */
  SUCCESS = "SUCCESS",
  /** TOO_LATE_TO_CHANGE - The customer is too established to change their country. */
  TOO_LATE_TO_CHANGE = "TOO_LATE_TO_CHANGE",
  /** COUNTRY_NOT_SUPPORTED - The country was available in the list but we don't support it yet. */
  COUNTRY_NOT_SUPPORTED = "COUNTRY_NOT_SUPPORTED",
}

export interface GetStatementRequest {
  request_context?: RequestContext;
  /**
   * A timestamp like 2016-01-01T00:00:00Z, represented as milliseconds since 1970-01-01 UTC. The
   * time in this timestamp must 00:00:00 UTC, and the day of month must be 1.
   */
  begin_date?: number;
}

export interface GetStatementResponse {
  response_context?: ResponseContext;
  /**
   * Deprecated in favor of customer_statement_data
   *
   * @deprecated
   */
  balance_snapshots?: BalanceSnapshot[];
  /** @deprecated */
  transactions?: StatementTransaction[];
  /** @deprecated */
  fees_paid_this_month?: Money;
  /** @deprecated */
  fees_paid_this_year?: Money;
  /** @deprecated */
  fees_start_date?: string;
  /** Full statement data for this customer. */
  customer_statement_data?: GetStatementResponseStatementData;
  /**
   * Full statement data for customers who this customer is sponsoring, i.e. teens with
   * account sponsorship by this customer.
   */
  sponsored_customers_statement_data?: GetStatementResponseSponsoredStatementData[];
}

export interface GetStatementResponseStatementData {
  /**
   * The stored value balance at 00:00:00 on each day, starting on the first of the requested month,
   * and continuing to the first of the next month. For example, for January 2016 this contains 32
   * elements from 2016-01-01 through 2016-02-01.
   */
  balance_snapshots?: BalanceSnapshot[];
  transactions?: StatementTransaction[];
  fees_paid_this_month?: Money;
  fees_paid_this_year?: Money;
  /**
   * The date that we will begin imposing Cash App ATM Fees, represented at month and day, i.e.
   * "May 30" or "June 1", etc.
   * This will display as part of a disclosure on a statement before the fees are imposed.
   * TODO(worstell): Remove this once it is no longer needed.
   */
  fees_start_date?: string;
}

export interface GetStatementResponseSponsoredStatementData {
  full_name?: string;
  cashtag?: string;
  statement_data?: GetStatementResponseStatementData;
}

/**
 * Should be done once the address has been verified or customer has had opportunity
 * to pick a final address.
 */
export interface CreateCheckRecipientRequest {
  full_name?: string;
  final_postal_address?: PostalAddress;
}

export interface CreateCheckRecipientResponse {
  status?: CreateCheckRecipientResponseStatus;
  customer?: UiCustomer;
}

export enum CreateCheckRecipientResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
}

/** Use this to add email address and phone number. */
export interface SupplyCheckRecipientContactsRequest {
  customer?: UiCustomer;
}

export interface SupplyCheckRecipientContactsResponse {
  status?: SupplyCheckRecipientContactsResponseStatus;
}

export enum SupplyCheckRecipientContactsResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
}

export interface GetCheckAddressRequest {
  /** token from UiCheckAddress */
  token?: string;
}

export interface GetCheckAddressResponse {
  status?: GetCheckAddressResponseStatus;
  postal_address?: PostalAddress;
}

export enum GetCheckAddressResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
}

export interface UpdateCheckRecipientRequest {
  /** references UiCustomer.id. */
  customer_id?: string;
  full_name?: string;
  postal_address?: PostalAddress;
}

export interface UpdateCheckRecipientResponse {
  status?: UpdateCheckRecipientResponseStatus;
  customer?: UiCustomer;
}

export enum UpdateCheckRecipientResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
}

export interface GetIssuedCardRequest {
  /**
   * If the app has persisted an issued card, it should provide its version token when requesting
   * an update. This allows the server to skip calling Marqeta if the card hasn't changed. And
   * allows the server to tell the app to discard it if it should now be locked.
   */
  version_token?: string;
}

export interface GetIssuedCardResponse {
  status?: GetIssuedCardResponseStatus;
  /**
   * Will be redacted if the issued card is locked. In this case, the app should follow the
   * scenario plan to get the unredacted version.
   */
  issued_card?: IssuedCard;
  /** A message to display to the customer. Can be non-null for either success or failure. */
  message?: string;
  /** Includes additional blockers for fetching the issued card. */
  scenario_plan?: ScenarioPlan;
  /** When true, the app should erase the issued card that it has persisted. */
  evict_existing_issued_card?: boolean;
}

export enum GetIssuedCardResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /**
   * CONCURRENT_MODIFICATION - Concurrent modification is returned when a request requires a passcode and the app should
   * have called VerifyPasscode instead. Apps should refresh profile then begin the flow again.
   */
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
  /**
   * FAILURE - The request failed. If a scenario plan is included, the app should follow it. If a message
   * is included, the app should display it.
   */
  FAILURE = "FAILURE",
}

/** @deprecated */
export interface SetIssuedCardEnabledRequest {
  /** True to enable the card, false to disable it. */
  enabled?: boolean;
  /** The token of the issued card to enable/disable. */
  issued_card_token?: string;
  request_context?: RequestContext;
}

/** @deprecated */
export interface SetIssuedCardEnabledResponse {
  response_context?: ResponseContext;
  status?: SetIssuedCardEnabledResponseStatus;
}

export enum SetIssuedCardEnabledResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /**
   * CONCURRENT_MODIFICATION - Concurrent modification is returned when a request requires a passcode and the app should
   * have called VerifyPasscode instead. It also occurs when the issued card token is not valid.
   * Apps should refresh profile then begin the flow again.
   */
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
  /**
   * FAILURE - The request failed. If a scenario plan is included, the app should follow it. If a message
   * is included, the app should display it.
   */
  FAILURE = "FAILURE",
}

/** @deprecated */
export interface SetIssuedCardLockRequest {
  /** True to lock the card, false to unlock it. */
  locked?: boolean;
}

/** @deprecated */
export interface SetIssuedCardLockResponse {
  status?: SetIssuedCardLockResponseStatus;
  /** The updated issued card. */
  issued_card?: IssuedCard;
  /** A message to display to the customer. Can be non-null for either success or failure. */
  message?: string;
  /** Includes additional blockers, such as a ConfirmBlocker with requires_passcode. */
  scenario_plan?: ScenarioPlan;
}

export enum SetIssuedCardLockResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /**
   * CONCURRENT_MODIFICATION - Concurrent modification is returned when a request requires a passcode and the app should
   * have called VerifyPasscode instead. Apps should refresh profile then begin the flow again.
   */
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
  /**
   * FAILURE - The request failed. If a scenario plan is included, the app should follow it. If a message
   * is included, the app should display it.
   */
  FAILURE = "FAILURE",
}

export interface VerifyDeviceRequest {
  challengeToken?: string;
  /**
   * Deprecated in favor of SafetyNetAttestation
   *
   * @deprecated
   */
  safety_net_jws_result?: string;
  /**
   * Deprecated in favor of SafetyNetAttestation
   *
   * @deprecated
   */
  play_services_status_code?: number;
  /**
   * Deprecated in favor of SafetyNetAttestation
   *
   * @deprecated
   */
  safety_net_status_code?: number;
  /**
   * The SafetyNet attestation result the client got (Android clients only).
   * This field is repeated to reflect any retries the client might have done to get the result.
   * the attestation result also contains a timestamp so results can be sorted.
   *
   * Deprecated in 3.93. Use play_integrity_attestation.
   *
   * @deprecated
   */
  safety_net_attestation?: SafetyNetAttestation[];
  /** App Attest attestation result the client got (iOS clients only). */
  app_attestation?: AppAttestAttestation;
  /** The Play Integrity attestation result the client got (Android clients only). */
  play_integrity_attestation?: PlayIntegrityAttestation;
}

export interface VerifyDeviceResponse {
}

export interface VerifyGovernmentIdRequest {
  request_context?: RequestContext;
  /** The payments being acted upon. */
  payment_tokens?: string[];
  /** The transfer being acted upon. */
  transfer_token?: string;
  /**
   * The client recognizes the front and back and sends the image to the server
   * for extra validation.
   */
  front_image_bytes?: Uint8Array;
  back_image_bytes?: Uint8Array;
  mime_type?: string;
  /**
   * The text recognized from the PDF-417 barcode on the back of the DL.
   * If not set, the client leaves recognition completely to the server.
   */
  pdf_417_text?: string;
  /**
   * Whether the front image was auto captured or manually when the
   * user hit a button. Useful to validate performance of client-side
   * image quality filters like edge-detection, face detection, etc.
   */
  front_manually_captured?: boolean;
  /**
   * Whether the back image was auto captured or manually when the
   * user hit a button. Useful to validate performance of client-side
   * image quality filters like edge-detection, face detection, etc.
   */
  back_manually_captured?: boolean;
  /**
   * The experiment/treatment applied client-side.
   * Useful to validate performance of client-side experiments.
   */
  treatment?: string;
}

export interface VerifyGovernmentIdResponse {
  response_context?: ResponseContext;
  status?: VerifyGovernmentIdResponseStatus;
}

export enum VerifyGovernmentIdResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

export interface ProvisionApplePayRequest {
  /** An array of data for encrypted certificates used to provision an Apple Pay instrument */
  certificates?: Uint8Array[];
  /** The nonce data provided by the client for this request */
  nonce?: Uint8Array;
  /** The nonce signature provided by client for this request */
  nonce_signature?: Uint8Array;
  request_context?: RequestContext;
}

export interface ProvisionApplePayResponse {
  /** Status of the result */
  status?: ProvisionApplePayResponseStatus;
  /**
   * The encrypted details from the card issuer. Card details should be a JSON object
   * with the keys: name, primaryAccountNumber, expiration.
   */
  encrypted_card_details?: Uint8Array;
  /** The ephemeral public key from the card issuer */
  ephemeral_public_key?: Uint8Array;
  /** The activation data provided by the card issuer */
  activation_data?: Uint8Array;
  /** The wrapped key provided by the card issuer */
  wrapped_key?: Uint8Array;
  /** A failure message, populated if the Status is FAILED. */
  failure_message?: string;
  response_context?: ResponseContext;
}

export enum ProvisionApplePayResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The request was successful, the card has been provisioned to Apple Pay. */
  SUCCESS = "SUCCESS",
  /** FAILED - The issuer was unable to fulfil the request with the provided information */
  FAILED = "FAILED",
}

export interface ProvisionDigitalWalletTokenRequest {
  provisioning_request?: DigitalWalletTokenProvisioningRequestData;
  request_context?: RequestContext;
}

export interface ProvisionDigitalWalletTokenResponse {
  provisioning_response?: DigitalWalletTokenProvisioningResponseData;
  response_context?: ResponseContext;
}

/**
 * Request to activate a card that was added to a digital wallet directly rather than from Cash
 * (e.g. the yellow flow for Apple Pay).
 */
export interface ActivateDigitalWalletRequest {
  request_context?: RequestContext;
  /**
   * The issued card token of the card that's been added to the wallet.
   *
   * @deprecated
   */
  issued_card_token?: string;
  /**
   * The FPAN token used to identify the issued card in the wallet.
   *
   * @deprecated
   */
  pan_reference_id?: string;
  digital_wallet_cards?: ActivateDigitalWalletRequestDigitalWalletCardData[];
}

export interface ActivateDigitalWalletRequestDigitalWalletCardData {
  /** Identifier of the issued card. This is shared between wallets. */
  fpan_id?: string;
  fpan_last_four?: string;
  /** Identifier for the card instance on the device. */
  dpan_id?: string;
  dpan_last_four?: string;
  /** The issued card token associated with this card. */
  issued_card_token?: string;
}

export interface ActivateDigitalWalletResponse {
  response_context?: ResponseContext;
}

export interface CompleteApplePayProvisioningRequest {
  request_context?: RequestContext;
  /** The result of provisioning Apple Pay. */
  result?: ApplePayProvisioningResult;
}

export interface CompleteApplePayProvisioningResponse {
  response_context?: ResponseContext;
}

export interface CompleteDigitalWalletTokenProvisioningRequest {
  request_context?: RequestContext;
  completion_request?: DigitalWalletTokenProvisioningCompletionData;
}

export interface CompleteDigitalWalletTokenProvisioningResponse {
  response_context?: ResponseContext;
}

export interface SelectOptionRequest {
  request_context?: RequestContext;
  /**
   * Pass in the action provided in SelectionOption.
   * Also set ClientScenario on the request header if provided in SelectionOption.
   */
  action?: string;
  /** Payment tokens if call is originating from a flow which has payment context. */
  payment_tokens?: string[];
  /** Transfer token if call is originating from a flow which has transfer context. */
  transfer_token?: string;
}

export interface SelectOptionResponse {
  response_context?: ResponseContext;
  status?: SelectOptionResponseStatus;
  /**
   * Scenario plan with blockers. Deprecated in v2.18 in favor of ResponseContext.
   *
   * @deprecated
   */
  scenario_plan?: ScenarioPlan;
}

export enum SelectOptionResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
}

/**
 * Submits the contents of a FormBlocker to the server.
 *
 * The contents of all customer-editable elements are included in the `results` field, with each
 * one identifiable using the unique ID that was specified in the FormBlocker for that element.
 *
 * This request will be triggered by the customer tapping a button which has a `BlockerAction`
 * type of `SubmitAction`. The ID from that action should be copied into the `action_id` field.
 * If multiple buttons on a form can be used to submit (likely with different customer intents)
 * then this ID can be used to determine which action the customer intended.
 */
export interface SubmitFormRequest {
  request_context?: RequestContext;
  /** The identifier of the `BlockerAction` that the user tapped to submit. */
  action_id?: string;
  /** The data entered by the customer in all input fields for this form. */
  results?: SubmitFormRequestElementResult[];
}

export interface SubmitFormRequestElementResult {
  /** The unique identifier for the element within the form for which this value was entered. */
  id?: string;
  address_result?: SubmitFormRequestElementResultAddressResult | undefined;
  text_input_result?: SubmitFormRequestElementResultTextInputResult | undefined;
  option_picker_result?: SubmitFormRequestElementResultOptionPickerResult | undefined;
  cashtag_result?: SubmitFormRequestElementResultCashtagResult | undefined;
  date_input_result?: SubmitFormRequestElementResultDateInputResult | undefined;
  money_input_result?: SubmitFormRequestElementResultMoneyInputResult | undefined;
  emoji_picker_result?: SubmitFormRequestElementResultEmojiPickerResult | undefined;
  merchant_transaction_picker_result?: SubmitFormRequestElementResultMerchantTransactionOptionPickerResult | undefined;
  webview_result?: WebViewCallbackInputs | undefined;
}

export interface SubmitFormRequestElementResultAddressResult {
  address?: GlobalAddress;
}

export interface SubmitFormRequestElementResultCashtagResult {
  /** The Cashtag value entered by the customer. This should *not* include the prefix symbol. */
  cashtag?: string;
}

/**
 * For submitting the date entered by the customer.
 *
 * This date is sent as separate day/month/year components rather than an ISO 8601 string
 * so that the values exactly represent what the customer entered and there is no confusion
 * about time zones.
 */
export interface SubmitFormRequestElementResultDateInputResult {
  day?: number;
  month?: number;
  year?: number;
}

export interface SubmitFormRequestElementResultMoneyInputResult {
  amount?: Money;
}

export interface SubmitFormRequestElementResultTextInputResult {
  input_field_values?: string[];
}

export interface SubmitFormRequestElementResultOptionPickerResult {
  option_id?: string;
}

export interface SubmitFormRequestElementResultEmojiPickerResult {
  selection?: string;
}

export interface SubmitFormRequestElementResultMerchantTransactionOptionPickerResult {
  selected_transactions?: string[];
}

export interface SubmitFormResponse {
  response_context?: ResponseContext;
}

export interface CloseAccountRequest {
}

export interface CloseAccountResponse {
}

export interface ResetDeviceRequest {
}

export interface ResetDeviceResponse {
}

export interface GetWebLoginConfigRequest {
}

export interface GetWebLoginConfigResponse {
  web_login_config?: WebLoginConfig;
}

export interface ReportAbuseRequest {
  request_context?: RequestContext;
  /** true to block, false to unblock */
  block?: boolean;
  /** token of the customer to be blocked/unblocked */
  customer_id?: string;
  /** payment through which the block request was initiated. */
  payment_token?: string;
}

export interface ReportAbuseResponse {
  response_context?: ResponseContext;
}

export interface ConfirmIosDonationRequest {
  request_context?: RequestContext;
  /** The payments being acted upon. */
  payment_tokens?: string[];
}

export interface ConfirmIosDonationResponse {
  response_context?: ResponseContext;
}

export interface SetCampaignSupporterDataRequest {
  request_context?: RequestContext;
  supporter_data?: CampaignSupporterData;
  /** The payments being acted upon. */
  payment_tokens?: string[];
}

export interface SetCampaignSupporterDataResponse {
  response_context?: ResponseContext;
}

export interface SetCardCustomizationRequest {
  request_context?: RequestContext;
  /** The image bytes and mime type for a card signature. */
  image_bytes?: Uint8Array;
  mime_type?: string;
  /**
   * What the card should look like. Only choices currently are a white vs a black card.
   *
   * @deprecated
   */
  card_theme?: CardTheme;
  /** What the card should look like when resolving a CardDesignBlocker. */
  card_design_theme?: CardDesignBlockerCardTheme;
  /** Token for the card theme that should be used. */
  card_theme_token?: string;
  /** The touch data for the customer's signature. */
  touch_data?: TouchData;
  /** Whether or not the server should add the cashtag to the cards customization. */
  include_cashtag?: boolean;
  /** An indicator for identifying whether it is the legacy or full-face customization. */
  customization_area?: CardCustomizationDataCustomizationArea;
}

export interface SetCardCustomizationResponse {
  response_context?: ResponseContext;
  status?: SetCardCustomizationResponseStatus;
}

export enum SetCardCustomizationResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /** FAILURE - If we deem the image inappropriate or invalid, we can require the customer to sign again. */
  FAILURE = "FAILURE",
}

export interface VerifyQrCodeRequest {
  request_context?: RequestContext;
  qr_code_data?: string;
  /**
   * An indicator if the customer click the missing QR code button, the server would need to give a
   * locate CVV blocker to the client.
   */
  missing_qr_code_to_locate_cvv?: boolean;
  /**
   * If customer skip scanning QR code
   *
   * @deprecated
   */
  skip_blocker?: boolean;
}

export interface VerifyQrCodeResponse {
  response_context?: ResponseContext;
  status?: VerifyQrCodeResponseStatus;
}

export enum VerifyQrCodeResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

export interface VerifyPasscodeAndExpirationRequest {
  request_context?: RequestContext;
  passcode?: string;
  expiration_date?: string;
}

export interface VerifyPasscodeAndExpirationResponse {
  response_context?: ResponseContext;
  status?: VerifyPasscodeAndExpirationResponseStatus;
}

export enum VerifyPasscodeAndExpirationResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

export interface VerifyMagicLinkRequest {
  request_context?: RequestContext;
  verification_token?: string;
  customer_requested_deny_attempt?: boolean;
}

export interface VerifyMagicLinkResponse {
  response_context?: ResponseContext;
  status?: VerifyMagicLinkResponseStatus;
}

export enum VerifyMagicLinkResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  INVALID_APP_TOKEN = "INVALID_APP_TOKEN",
  INVALID_VERIFICATION_TOKEN = "INVALID_VERIFICATION_TOKEN",
  EXPIRED_VERIFICATION_TOKEN = "EXPIRED_VERIFICATION_TOKEN",
  TOO_MANY_FAILED_ATTEMPTS = "TOO_MANY_FAILED_ATTEMPTS",
  NOT_ELIGIBLE = "NOT_ELIGIBLE",
}

export interface GetUnredactedIssuedCardRequest {
  passcode?: string;
  passcode_token?: string;
}

export interface GetUnredactedIssuedCardResponse {
  status?: GetUnredactedIssuedCardResponseStatus;
  /** The unredacted issued card. */
  issued_card?: IssuedCard;
  /** An error message, will be null if call was successful. */
  failure_message?: string;
  passcode_token?: string;
}

export enum GetUnredactedIssuedCardResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  INVALID_PASSCODE = "INVALID_PASSCODE",
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
  FAILURE = "FAILURE",
}

export interface UploadFileRequest {
  request_context?: RequestContext;
  /** Base64 encoded file. Used by the web app. */
  file_data?: string;
  /** Raw file bytes. Used by the native apps. */
  file_bytes?: Uint8Array;
  /** If the file is an image, the mime type of the file. */
  mime_type?: string;
  /** The category of file being uploaded, such as a selfie, W2, SSN card, etc. */
  category?: FileCategory;
  /** Payment tokens if call is originating from a flow which has payment context. */
  payment_tokens?: string[];
  /** Transfer token if call is originating from a flow which has transfer context. */
  transfer_token?: string;
  /** Fraud payload. Used by Bouncer Integration for example. */
  fraud_payload?: string;
  /** The last four digits of a PAN, if the file being uploaded is a card scan. */
  last_four?: string;
}

export interface UploadFileResponse {
  response_context?: ResponseContext;
}

/**
 * Request to get service status information tailored to the calling customer.
 * Apps consume this to show messages when partial outages or service interruptions occur.
 */
export interface GetServiceMessageRequest {
}

export interface GetServiceMessageResponse {
  service_message?: ServiceMessage;
}

/** Request the exchange rates of other exchangeable currencies (ie. Bitcoin) */
export interface GetExchangeDataRequest {
  /** Symbols to be quoted. If not present, assumed to be { BTC } */
  symbols?: string[];
  /** The quote currency. If not present, assumed to be USD. */
  quote_currency_code?: CurrencyCode;
}

export interface GetExchangeDataResponse {
  exchange_data?: ExchangeData;
  equity_data?: ExchangeData[];
}

/**
 * Request the historical prices for a specified range for the purpose of rendering into graphs,
 * client-side.
 */
export interface GetHistoricalExchangeDataRequest {
  /** The range to represent the historical data. */
  historical_range?: HistoricalRange;
  /** The base currency. */
  base_currency_code?: string;
  /** The currency in which the price is quoted. */
  quote_currency_code?: string;
}

export interface GetHistoricalExchangeDataResponse {
  price_history?: PriceHistory;
}

/**
 * The request message for obtaining a contract for conducting a currency exchange. Exchange
 * contracts can be quoted in one of two ways: 1. An amount can be specified in source currency
 * or 2. an amount can be specified in target currency. But both should not be specified.
 *
 * For instance, in any given request, only the source_cents or target_cents should be specified.
 * If both are specified, the server will return MALFORMED_REQUEST.
 *
 * This allows the customer to say, request to be able to buy "1 BTC" or instead, buy "$1000 worth
 * of BTC". Whichever field is specified, will imply the directionality of the quote being
 * requested.
 *
 * Both source_currency_code and target_currency_code msut be specified in any case.
 */
export interface GetExchangeContractRequest {
  source_currency_code?: string;
  target_currency_code?: string;
  source_cents?: number;
  target_cents?: number;
  /**
   * The order configuration if this is a custom order. Clients are to set this field for the
   * `get-exchange-contract` call made just before calling `execute-contract`, once the customer
   * finished setting up its purchase. Likewise, clients can leave it null if the customer is still
   * at the beginning of the custom order flow.
   */
  custom_order?: CustomOrder;
}

/** The response message returned after requesting a contract. */
export interface GetExchangeContractResponse {
  status_result?: StatusResult;
  /** The created exchange contract containing a price quote and executable contract_token. */
  exchange_contract?: ExchangeContract;
  /** A server message with extra user-readable details of any error that may have occurred. */
  message?: string;
}

/**
 * The request message to execute a contract obtained by the GetExchangeContract RPC call. Assuming
 * an unexpired and valid contract, the contract should be executed by the server.
 */
export interface ExecuteContractRequest {
  request_context?: RequestContext;
  /** The contract token the client wants to execute */
  contract_token?: string;
  /**
   * The amount of source currency that will be deducted from the customers balance with this
   * exchange.
   */
  source_amount?: Money;
  /**
   * The amount of source currency that will be credited from the customers balance with this
   * exchange.
   */
  target_amount?: Money;
  customer_preferred_display_currency?: ExecuteContractRequestCustomerPreferredDisplayCurrency;
  /**
   * The source instrument token to pay for the purchase. Note: the source token currency must
   * match the currency pair in the quoted contract.
   */
  source_token?: string;
  /**
   * The destination instrument to deposit the purchase. Note: the target token currency must
   * match the currency pair in the quoted contract.
   */
  target_token?: string;
  /** Synchronously advance payment through passcode confirmation using this token. */
  passcode_token?: string;
  /** The recurring schedule if this is a recurring purchase. */
  recurring_schedule?: RecurringSchedule;
  /** The custom order if this is a custom purchase. */
  custom_order?: CustomOrder;
}

/**
 * When specifying the amount of the contract, the customer can select the currency it's shown
 * in (e.g. in the slider view). We want to use the same currency in the confirmation blocker,
 * so the client needs to specify if the currency used matched the source balance or the target
 * balance.
 */
export enum ExecuteContractRequestCustomerPreferredDisplayCurrency {
  SAME_AS_SOURCE = "SAME_AS_SOURCE",
  SAME_AS_TARGET = "SAME_AS_TARGET",
}

/** The response message returned by calling the ExecuteContract RPC. */
export interface ExecuteContractResponse {
  response_context?: ResponseContext;
  /**
   * Deprecated in v2.20, use response_context instead *
   *
   * @deprecated
   */
  status_result?: StatusResult;
  /** A server message with extra user-readable details of any error that may have occurred. */
  message?: string;
  /** The updated source instrument */
  source_balance_instrument?: Instrument;
  /** The updated target instrument */
  balance_instrument?: Instrument;
}

export interface InitiateCryptocurrencyTransferRequest {
  request_context?: RequestContext;
  /** The instrument token for the balance to get the BTC from. */
  balance_token?: string;
  /**
   * The address of the wallet to take the BTC from.
   *
   * @deprecated
   */
  source_wallet_address?: string;
  /** The address of the wallet to transfer the BTC to. */
  target_wallet_address?: string;
  /** The amount in BTC to transfer. */
  amount?: Money;
  /** An external id provides idempotence if a request is retried. */
  external_id?: string;
  /** Synchronously advance payment through passcode confirmation using this token. */
  passcode_token?: string;
  type?: InitiateCryptocurrencyTransferRequestType;
  /** Transaction token of the deposit to be reversed through the withdrawal being initiated. */
  deposit_transaction_token?: string;
}

export enum InitiateCryptocurrencyTransferRequestType {
  BITCOIN_BLOCKCHAIN = "BITCOIN_BLOCKCHAIN",
  LIGHTNING = "LIGHTNING",
}

export interface InitiateCryptocurrencyTransferResponse {
  response_context?: ResponseContext;
  status?: InitiateCryptocurrencyTransferResponseStatus;
}

export enum InitiateCryptocurrencyTransferResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
}

/** Temporary message for the migration from Franklin to Moongate */
export interface WrappedInitiateCryptocurrencyTransferRequest {
  /** The actual request */
  request?: InitiateCryptocurrencyTransferRequest;
  customer_token?: string;
  flow_token?: string;
  app_version?: AppVersion;
  call_context?: CallContext;
}

export interface GenerateLightningInvoiceRequest {
  amount?: Money;
  /** The instrument token for the balance to get the BTC from. */
  balance_token?: string;
}

export interface GenerateLightningInvoiceResponse {
  invoice?: string;
  status?: GenerateLightningInvoiceResponseStatus;
}

export enum GenerateLightningInvoiceResponseStatus {
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

/** Request all of the feature flags which the client can modify. */
export interface GetFeatureFlagsRequest {
}

export interface GetFeatureFlagsResponse {
  /** All of the feature flags which the client can modify, along with their current values. */
  feature_flags?: FeatureFlag[];
}

/** Change a subset of the feature flags. */
export interface SetFeatureFlagsRequest {
  /** A list of only the flags that have changed along with their new value. */
  feature_flags?: FeatureFlag[];
}

export interface OAuthResolveFlowRequest {
  request_context?: RequestContext;
  /** The name of the OAuth flow to execute. */
  flow_type?: OAuthConfigFlowType;
  /** Url containing the oauth flow response. */
  url_contents?: string;
}

export interface OAuthResolveFlowResponse {
  response_context?: ResponseContext;
  status?: OAuthResolveFlowResponseStatus;
}

export enum OAuthResolveFlowResponseStatus {
  /** INVALID - The request was invalid. */
  INVALID = "INVALID",
  /** SUCCESS - The OAuth handshake was successful. Proceed with success flow. */
  SUCCESS = "SUCCESS",
  /** FAILURE - Unable to get the long term access token. Proceed with error flow. */
  FAILURE = "FAILURE",
  /** TOO_MANY_ATTEMPTS - Attempted too many times. Try later. */
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
  CONCURRENT_MODIFICATION = "CONCURRENT_MODIFICATION",
}

export interface SetFeatureFlagsResponse {
  /**
   * All of the feature flags which the client might can modify, along with their current values.
   * This list should be identical to the one returned in GetFeatureFlagsResponse.
   */
  feature_flags?: FeatureFlag[];
}

/**
 * Gets the effective limits for each action passed in.
 * The effective limit is the maximum amount the action can use and still pass. The effective limit
 * can incorporate a variety of the customer's activity and balance limits.
 * e.g. for Add Cash, the effective limit would be the most the customer can add without exceeding
 * the balance limit and without exceeding their daily/weekly spending limits.
 */
export interface GetEffectiveCustomerLimitsRequest {
  limited_actions?: LimitedAction[];
}

export interface GetEffectiveCustomerLimitsResponse {
  limits?: GetEffectiveCustomerLimitsResponseLimitForAction[];
}

export interface GetEffectiveCustomerLimitsResponseLimitForAction {
  action?: LimitedAction;
  /**
   * The maximum amount the user may specify for the action. If 0, the customer has already met
   * or exceeded their limit and cannot perform the action.
   */
  limit?: Money;
  /** Error message to display when customer's limit is 0. */
  limit_exceeded_message?: string;
}

export interface GetRewardsRequest {
}

export interface GetRewardsResponse {
  rewards_data?: RewardsData;
}

export interface AddOrUpdateRewardRequest {
  /**
   * The token of the 'reward selection' whose reward is being replaced.
   * NOTE: This is not the token of the reward that is being replaced.
   */
  replace_selected_reward_token?: string;
  /**
   * The reward that is replacing the existing selected_reward. If null, the selected reward is
   * removed.
   */
  reward_token?: string;
  request_context?: RequestContext;
}

export interface AddOrUpdateRewardResponse {
  response_context?: ResponseContext;
}

export interface UnlockRewardRequest {
  /** The reward token corresponding to the Boost that the customer is trying to unlock. */
  reward_token?: string;
  request_context?: RequestContext;
}

export interface UnlockRewardResponse {
  response_context?: ResponseContext;
}

export interface SetLocationPermissionRequest {
  permitted?: boolean;
}

export interface SetLocationPermissionResponse {
}

export interface PublishGpsEventRequest {
  event?: GpsEvent;
}

export interface PublishGpsEventResponse {
}

export interface GetLocationPermissionRequest {
}

export interface GetLocationPermissionResponse {
  permitted?: boolean;
}

export interface GetLocationConfigRequest {
}

export interface GetLocationConfigResponse {
  time_interval_ms?: number;
  distance_filter_meters?: number;
}

export interface SetScheduledTransactionPreferenceRequest {
  request_context?: RequestContext;
  scheduled_transaction_preference?: ScheduledTransactionPreference;
}

export interface SetScheduledTransactionPreferenceResponse {
  response_context?: ResponseContext;
}

export interface SetLoyaltyNotificationPreferenceRequest {
  request_context?: RequestContext;
  notifications_enabled?: boolean;
}

export interface SetLoyaltyNotificationPreferenceResponse {
  response_context?: ResponseContext;
}

export interface GetEquityContractRequest {
  request_context?: RequestContext;
  usd_stored_value_token?: string;
  symbol?: string;
  amount?: number;
  amountType?: GetEquityContractRequestAmountType;
  type?: GetEquityContractRequestRequestType;
}

export enum GetEquityContractRequestRequestType {
  PURCHASE = "PURCHASE",
  SALE = "SALE",
}

export enum GetEquityContractRequestAmountType {
  SHARES = "SHARES",
  CURRENCY = "CURRENCY",
}

export interface GetEquityContractResponse {
  response_context?: ResponseContext;
  equityPurchaseContract?: GetEquityContractResponseEquityContract;
}

export interface GetEquityContractResponseEquityContract {
  symbol?: string;
  price_cents?: number;
  equity_purchase_contract_token?: string;
  expires_at?: number;
  dealt_amount?: number;
  settlement_amount?: number;
}

export interface ExecuteEquityContractRequest {
  request_context?: RequestContext;
  contract_token?: string;
}

export interface ExecuteEquityContractResponse {
  response_context?: ResponseContext;
}

export interface GetRewardProfileRequest {
  code?: string;
}

export interface GetRewardProfileResponse {
  title?: string;
  description?: string;
  display_name?: string;
  photo_url?: string;
  override_get_apps_url?: string;
}

export interface SuggestedRecipientsRequest {
}

export interface SuggestedRecipientsResponse {
  suggested_recipients?: CashCustomer[];
}

export interface GetTaxFormPrefillRequest {
}

/** Returns previously filled in tax information if available. */
export interface GetTaxFormPrefillResponse {
  name?: string;
  tin_status?: GetTaxFormPrefillResponseTinStatus;
  postal_address?: GlobalAddress;
  entity?: TaxEntity;
}

/**
 * The customer can update address information without resubmitting their TIN.
 * We don't send the actual TIN as it is sensitive PII.
 */
export enum GetTaxFormPrefillResponseTinStatus {
  MISSING = "MISSING",
  SSN_ON_FILE = "SSN_ON_FILE",
  EIN_ON_FILE = "EIN_ON_FILE",
}

export interface ShareReceiptRequest {
  request_context?: RequestContext;
  email?: string;
  /** The payments being acted upon. */
  payment_tokens?: string[];
}

export interface ShareReceiptResponse {
}

export interface SetTaxInformationRequest {
  request_context?: RequestContext;
  entity?: TaxEntity;
}

export interface SetTaxInformationResponse {
  response_context?: ResponseContext;
}

export interface SetIndividualTaxInformationRequest {
  request_context?: RequestContext;
  entity?: TaxEntity;
}

export interface SetIndividualTaxInformationResponse {
  response_context?: ResponseContext;
  status?: SetIndividualTaxInformationResponseStatus;
  /** Only set when status is ACCEPTED so client can show the W9 confirmation page. */
  tax_token?: string;
}

export enum SetIndividualTaxInformationResponseStatus {
  INVALID = "INVALID",
  /** SUCCESS - The TIN-Name pair is accepted by the server. */
  SUCCESS = "SUCCESS",
  /** MISMATCH - The Tin-Name pair failed the validation or TIN has not been issued. */
  MISMATCH = "MISMATCH",
  /** ERROR - Other errors when processing(verifying or saving) TIN-Name pair. */
  ERROR = "ERROR",
}

export interface ConfirmIndividualTaxInformationRequest {
  request_context?: RequestContext;
}

export interface ConfirmIndividualTaxInformationResponse {
  response_context?: ResponseContext;
}

export interface GetIndividualTaxInformationStatusRequest {
  request_context?: RequestContext;
}

export interface GetIndividualTaxInformationStatusResponse {
  response_context?: ResponseContext;
  tin_status?: GetIndividualTaxInformationStatusResponseStatus;
  /** Only set when status is VALID_NOT_CONFIRMED so client can show the W9 confirmation page. */
  tax_token?: string;
}

export enum GetIndividualTaxInformationStatusResponseStatus {
  INVALID = "INVALID",
  /** MISSING_OR_INVALID - The customer does not have TIN-Name pair or it is invalid */
  MISSING_OR_INVALID = "MISSING_OR_INVALID",
  /** VALID_NOT_CONFIRMED - The customer has a valid TIN-Name pair on file but has not yet confirm the terms. */
  VALID_NOT_CONFIRMED = "VALID_NOT_CONFIRMED",
  /** VALID_AND_CONFIRMED - The customer has a valid TIN-Name pair on file and has confirmed the terms. */
  VALID_AND_CONFIRMED = "VALID_AND_CONFIRMED",
}

export interface OrderConfirmationRequest {
  request_context?: RequestContext;
}

export interface OrderConfirmationResponse {
  response_context?: ResponseContext;
}

/**
 * NOTE: The usage of this endpoint is unreliable, clients may drop the call due to network issues
 * and continue on after intercepting a deeplink.
 * Will be cleaned up after clients have eventstream2 support.
 */
export interface ReportDeeplinkRequest {
  /** The full url complete with any query parameters. */
  raw_url?: string;
  /** Free form string that indicates the referring website or app if known. */
  referrer?: string;
}

export interface ReportDeeplinkResponse {
}

export interface TriggerBalanceCheckRequest {
  /**
   * The amount that customer is going to send. It is fine if customer edits this amount
   * later and initiates a payment with a different amount. We won't trigger the balance check if
   * there is enough stored balance to cover the payment. The goal here is to reduce costs for idle
   * balance checks.
   */
  probable_payment_amount_cents?: number;
}

export interface CreateRedPocketEnvelopeRequest {
  request_context?: RequestContext;
  /** Total amount to split across recipients / packets. */
  amount?: Money;
  /** Optional, will split amount across all recipients if not specified. */
  packets?: number;
  recipients?: CashCustomer[];
  /** Used for idempotency. */
  external_id?: string;
  /** Free-form text field, populated by the initiator. */
  note?: string;
}

export interface CreateRedPocketEnvelopeResponse {
  response_context?: ResponseContext;
}

export interface OpenRedPocketEnvelopeRequest {
  /** RequestContext has list of payment tokens. */
  request_context?: RequestContext;
}

export interface OpenRedPocketEnvelopeResponse {
  response_context?: ResponseContext;
  success?: boolean;
  error_message?: string;
}

export interface TriggerBalanceCheckResponse {
}

export interface InitiateRemoteCheckDepositRequest {
  request_context?: RequestContext;
  check_front?: InitiateRemoteCheckDepositRequestImage;
  check_back?: InitiateRemoteCheckDepositRequestImage;
  amount?: Money;
}

export interface InitiateRemoteCheckDepositRequestImage {
  contents?: Uint8Array;
  mime_type?: string;
}

export interface InitiateRemoteCheckDepositResponse {
  response_context?: ResponseContext;
}

/**
 * Deprecated, previously used for a Pay with Cash implementation that was never rolled out.
 * Use GetFlowRequest instead.
 */
export interface GetEnrollmentRequest {
  /** @deprecated */
  request_context?: RequestContext;
  /**
   * Used for fetching data about an enrollment.
   *
   * @deprecated
   */
  token?: string;
}

/**
 * Deprecated, previously used for a Pay with Cash implementation that was never rolled out.
 * Use GetFlowResponse instead.
 */
export interface GetEnrollmentResponse {
  /** @deprecated */
  response_context?: ResponseContext;
  /**
   * Data about the account integration bound to this enrollment.
   *
   * @deprecated
   */
  account_integration?: AccountIntegration;
  /**
   * A URL used to redirect the client if enrollment is request is declined.
   *
   * @deprecated
   */
  deny_action_url?: string;
  /**
   * An accent color for the account integration, used for theming certain UI elements.
   *
   * @deprecated
   */
  accent_color?: string;
}

export interface ConfirmDisclosureRequest {
  request_context?: RequestContext;
}

export interface ConfirmDisclosureResponse {
  response_context?: ResponseContext;
}

export interface UpdatePaymentScheduleRequest {
  request_context?: RequestContext;
  /** Which payment schedules should be updated. */
  payment_schedule_tokens?: string[];
  /**
   * The updated status of this payment schedule.
   * NOTE: A payment can not be marked as COMPLETED,
   * and payments can not be set to ACTIVE if in a terminal state.
   */
  state?: PaymentScheduleState;
}

export interface UpdatePaymentScheduleResponse {
  status?: UpdatePaymentScheduleResponseStatus;
  response_context?: ResponseContext;
  /** The updated payment schedules related to the request. */
  payment_schedules?: UiPaymentSchedule[];
}

export enum UpdatePaymentScheduleResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  NOT_FOUND = "NOT_FOUND",
}

export interface AssignClaimableAssetRequest {
  request_context?: RequestContext;
  /** Unique identifier for the claimable asset. */
  token?: string;
  /**
   * Determines whether the entire collection of assets should be claimed,
   * or just the specific asset identified (default: true).
   */
  claim_entire_collection?: boolean;
}

export interface AssignClaimableAssetResponse {
  status?: AssignClaimableAssetResponseStatus;
  response_context?: ResponseContext;
  /** The number of assets claimed by the customer, for the request. */
  number_assets_claimed?: number;
}

export enum AssignClaimableAssetResponseStatus {
  UNAUTHORIZED = "UNAUTHORIZED",
  SUCCESS = "SUCCESS",
  NOT_FOUND = "NOT_FOUND",
  FAILURE = "FAILURE",
}

export interface GetBoostConfigRequest {
}

export interface GetBoostConfigResponse {
  /** Percentage (expressed in basis points) of a Boost's live period that the client should show additional hints about the upcoming expiry. */
  boost_expiration_hint_threshold_basis_points?: number;
  /**
   * Information for clients to render an upsell tile in the BTC/Investing tab. When this field
   *  is null, the tile should not be rendered.
   */
  btc_boost_upsell?: GetBoostConfigResponseBtcBoostUpsell;
}

export interface GetBoostConfigResponseBtcBoostUpsell {
  /**
   * Deprecated in favor of image.
   *
   * @deprecated
   */
  image_url?: string;
  title_text?: string;
  body_text?: string;
  primary_button?: GetBoostConfigResponseBtcBoostUpsellButton;
  image?: Image;
}

export interface GetBoostConfigResponseBtcBoostUpsellButton {
  label_text?: string;
  /**
   * When the button is tapped, we route to the appropriate action via a URL (e.g., a deep link
   *  https://internal.cash.app/dl/scenario/$name_of_scenario_plan.)
   */
  url?: string;
}

export interface HandleThreeDSChallengeOutcomeRequest {
  request_context?: RequestContext;
  /** 3D-Secure token that references the card that needs to be linked. */
  threeds_token?: string;
  status?: HandleThreeDSChallengeOutcomeRequestStatus;
}

export enum HandleThreeDSChallengeOutcomeRequestStatus {
  /**
   * CHALLENGE_COMPLETED - The NuData Client SDK has called the challenge completed callback indicating it has
   * finished processing it.
   */
  CHALLENGE_COMPLETED = "CHALLENGE_COMPLETED",
  /** CHALLENGE_ERROR - The user failed to provide valid challenge data and the challenge has failed to complete. */
  CHALLENGE_ERROR = "CHALLENGE_ERROR",
  UNEXPECTED_ERROR = "UNEXPECTED_ERROR",
}

export interface HandleThreeDSChallengeOutcomeResponse {
  response_context?: ResponseContext;
  status?: HandleThreeDSChallengeOutcomeResponseStatus;
  /** Can be null if the failure is not related to a specific field, or related to multiple fields. */
  failure_field?: FieldName;
  /**
   * A token that can be supplied when initiating a payment to skip passcode confirmation for
   * that payment.
   */
  passcode_token?: string;
  /** This will contain the successfully recognized PAN only if linked using OCR. */
  unencrypted_pan_ocr_result?: string;
  /** The instrument that was linked. Only populated on successful links. */
  instrument?: Instrument;
  /**
   * The instrument to use when prompting the customer for passcode verification. Returning it here
   * saves the app from having to call GetProfile immediately after unlinking an instrument.
   */
  verification_instrument_token?: string;
}

export enum HandleThreeDSChallengeOutcomeResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /** FAILURE - The card could not be linked. */
  FAILURE = "FAILURE",
  /**
   * INSTRUMENT_TYPE_MISMATCH - The user attempted to link a credit card as a debit card or vice versa.
   * Only supported in v2.9+.
   */
  INSTRUMENT_TYPE_MISMATCH = "INSTRUMENT_TYPE_MISMATCH",
}

/**
 * Deprecated, previously used for a Pay with Cash implementation that was never rolled out.
 * Use GetFlowRequest instead.
 */
export interface ApproveAccountIntegrationRequest {
  /** @deprecated */
  token?: string;
  /** @deprecated */
  request_context?: RequestContext;
}

/**
 * Deprecated, previously used for a Pay with Cash implementation that was never rolled out.
 * Use GetFlowResponse instead.
 */
export interface ApproveAccountIntegrationResponse {
  /** @deprecated */
  code?: string;
  /** @deprecated */
  response_context?: ResponseContext;
}

export interface RevokeAccountIntegrationRequest {
  request_context?: RequestContext;
  /** Client Id of the integration to revoke. */
  client_id?: string;
  /** The resource identifier to revoke. */
  user_token?: string;
}

export interface RevokeAccountIntegrationResponse {
  response_context?: ResponseContext;
}

export interface SendTaxFormEmailRequest {
  request_context?: RequestContext;
  /** Unique identifier for the key of the requested tax form. */
  file_key?: string;
  type?: StatementType;
  document_date?: number;
  name?: string;
}

export interface SendTaxFormEmailResponse {
  response_context?: ResponseContext;
}

export interface SetSignatureRequest {
  request_context?: RequestContext;
  image_bytes?: Uint8Array;
  mime_type?: string;
}

export interface SetSignatureResponse {
  response_context?: ResponseContext;
  status?: SetSignatureResponseStatus;
}

export enum SetSignatureResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /** FAILURE - If we deem the image inappropriate or invalid, we can require the customer to sign again. */
  FAILURE = "FAILURE",
}

export interface SetAmountRequest {
  request_context?: RequestContext;
  amount_result?: SetAmountRequestAmountResult | undefined;
  percentage_result?: SetAmountRequestPercentageResult | undefined;
}

export interface SetAmountRequestAmountResult {
  amount?: Money;
}

export interface SetAmountRequestPercentageResult {
  percentage?: ScientificNumber;
}

export interface SetAmountResponse {
  response_context?: ResponseContext;
  status?: SetAmountResponseStatus;
}

export enum SetAmountResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

export interface SelectSponsorsRequest {
  request_context?: RequestContext;
  /**
   * upon cancellation, the empty list can be sent for sponsors since the customer will be allowed to
   * make a new selection
   * upon SELECTion and CONFIRMation of sponsors , selected sponsors are passed through this field
   */
  sponsors?: UiCustomer[];
  action?: SelectSponsorsRequestAction;
  /**
   * upon cancellation, the invite_code is need to cancel previously created invitation for OON
   * request
   * upon selection of sponsors, this can be null as invite_code will not have been created yet
   */
  invite_code?: string;
  /**
   * This field indicates that the customer has allowed cashapp to pull their contacts and populate
   * them as part of the sponsor options in the select sponsors screen. It is required for
   * the SELECT action when building the sponsor selection detail blockers
   */
  dependent_contacts_syncd?: boolean;
  /**
   * DO NOT USE YET! This field will eventually replace `sponsors` field
   * upon cancellation, the empty list can be sent for sponsors since the customer will be allowed to
   * make a new selection
   * upon SELECTion and CONFIRMation of sponsors , selected sponsors are passed through this field
   */
  selected_sponsors?: SelectSponsorsRequestSelectedSponsor[];
}

/** Actions that can be handled between SelectSponsors and SelectSponsorsDetail blockers */
export enum SelectSponsorsRequestAction {
  /** SELECT - action sent when sponsor is selected from SelectSponsors screen to present SelectSponsorsDetail confirmation screen */
  SELECT = "SELECT",
  /** CANCEL - action sent when sponsor selection is canceled from SelectSponsorsDetail confirmation screen */
  CANCEL = "CANCEL",
  /** CONFIRM - action sent when sponsor selection is confirmed from SelectSponsorsDetail confirmation screen */
  CONFIRM = "CONFIRM",
}

/**
 * SelectedSponsor will hold not just the sponsor UICustomer object but also attributes like
 * is_in_contacts and is_added_to_favorites of the relationships between the sponsor and the
 * dependent from whom the request originated
 */
export interface SelectSponsorsRequestSelectedSponsor {
  sponsor?: UiCustomer;
  is_in_contacts?: boolean;
  is_added_to_favorites?: boolean;
}

export interface SelectSponsorsResponse {
  response_context?: ResponseContext;
}

export interface FinishCreditMultiStepLoadRequest {
  request_context?: RequestContext;
  /** user clicked notify me */
  requested_to_be_notified?: boolean;
}

export interface FinishCreditMultiStepLoadResponse {
  response_context?: ResponseContext;
}

export interface CreditMultiStepLoadPollingRequest {
  request_context?: RequestContext;
}

export interface CreditMultiStepLoadPollingResponse {
  response_context?: ResponseContext;
  /**
   * The status of the operation for which the multi-step load is polling. If `STILL_PROCESSING`,
   * the caller should continue polling.
   */
  status?: CreditMultiStepLoadPollingResponseStatus;
}

export enum CreditMultiStepLoadPollingResponseStatus {
  IN_PROGRESS = "IN_PROGRESS",
  FINISHED = "FINISHED",
}

export interface LoanAmountPickerRequest {
  request_context?: RequestContext;
  /** user clicked a value in cents */
  selected_amount_cents?: number;
}

export interface LoanAmountPickerResponse {
  response_context?: ResponseContext;
}

export interface CreditNullStateButtonRequest {
  request_context?: RequestContext;
}

export interface CreditNullStateButtonResponse {
  response_context?: ResponseContext;
}

export interface CreditFirstTimeBorrowRequest {
  request_context?: RequestContext;
  requested_amount?: Money;
}

export interface CreditFirstTimeBorrowResponse {
  response_context?: ResponseContext;
}

/** The request to complete a client scenario. */
export interface FinishPinwheelLinkRequest {
  request_context?: RequestContext;
  /** Use the one from SDK initialization */
  link_token?: string;
  on_login_response?: PinwheelLinkOnLoginResponse | undefined;
  on_exit_response?: PinwheelLinkOnExitResponse | undefined;
  on_success_response?: PinwheelLinkOnSuccessResponse | undefined;
}

export interface FinishPinwheelLinkResponse {
  response_context?: ResponseContext;
}

export enum FinishPinwheelLinkResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
}

export interface FinishTutorialRequest {
  request_context?: RequestContext;
}

export interface FinishTutorialResponse {
  response_context?: ResponseContext;
}

export interface UnlinkBusinessRequest {
  request_context?: RequestContext;
  grant_id?: string;
}

export interface UnlinkBusinessResponse {
  response_context?: ResponseContext;
}

export interface ApproveCashAppPayRequest {
  request_context?: RequestContext;
  /**
   * The raw deep link url from PayKit that contains the customer request id.
   * Ex: https://cashstaging.app/f/GRANTLY_MANAGED_GRANT%3Frequest_id=GRR_qrtqh3hxwyjaf5ry4mmhvsaa-sefvwn&method=mobile_url
   */
  request_auth_flow_triggers_mobile_url?: string;
  /** The token identifying the referring funnel this request was initiated from. Optional. */
  referrer_flow_token?: string;
}

export interface ApproveCashAppPayResponse {
  response_context?: ResponseContext;
}

/** Use this Api to submit the selected payroll provider from the payroll provider search blocker. */
export interface SubmitPayrollProviderSelectionRequest {
  request_context?: RequestContext;
  query?: string;
  selected_payroll_provider?: PayrollProvider | undefined;
  selected_fallback_result?: boolean | undefined;
}

export interface SubmitPayrollProviderSelectionResponse {
  response_context?: ResponseContext;
}

/**
 * SetOnboardingContextRequest allows passing in a payload that allows
 * the server to record the intent of a particular onboarding flow (flow-token passed in header)
 * and decide what experience to present to the user.
 */
export interface SetOnboardingContextRequest {
  onboarding_payload?: string;
}

export interface SetOnboardingContextResponse {
}

export interface FinishInstantPayAutoAdvanceRequest {
  request_context?: RequestContext;
  /** user clicked notify me */
  requested_to_be_notified?: boolean;
}

export interface FinishInstantPayAutoAdvanceResponse {
  response_context?: ResponseContext;
}

export interface FinishInstantPayDirectDepositSwitchRequest {
  request_context?: RequestContext;
}

export interface FinishInstantPayDirectDepositSwitchResponse {
  response_context?: ResponseContext;
}

export interface PlaidLinkTokenCreateResponse {
  link_token?: string;
  expiration?: string;
  request_id?: string;
}

export interface PlaidLinkTokenCreateRequest {
  /**
   * Available from client versions 3.67+.
   * Indicates whether the customer should be allowed to manually link an account.
   */
  manual_ach_enabled?: boolean;
  /**
   * Available from client versions 3.67+.
   * The associated client scenario when invoked from a blocker flow.
   */
  client_scenario?: ClientScenario;
  /** Available from client versions 3.69+. */
  plaid_linking_config?: PlaidLinkingConfig;
}

/** Sent by the apps once the customer has tapped an item in an ActivityPickerBlocker. */
export interface SelectActivityRequest {
  request_context?: RequestContext;
  /** The id of the entity the customer selected from their activity feed. */
  entity_id?: string;
}

/** Sent by the server in response to a SelectActivityResponse. */
export interface SelectActivityResponse {
  response_context?: ResponseContext;
}

/** Sent by the apps when the customer submits a FilesetUploadBlocker. */
export interface SubmitFilesetRequest {
  request_context?: RequestContext;
  /** The identifier of the `BlockerAction` that the user tapped to submit. */
  action_id?: string;
  /** The tokens of the files being submitted. */
  file_tokens?: string[];
}

/** Sent by the server in response to a SubmitFilesetRequest. */
export interface SubmitFilesetResponse {
  response_context?: ResponseContext;
}

/**
 * This request will be triggered by the customer tapping a button which has a
 * `BlockerAction` type of `EndFlowAction` with a notify id.
 *
 * The header will also include the flow token and client scenario.
 */
export interface EndFlowRequest {
  request_context?: RequestContext;
  /** The identifier of the `BlockerAction` that the user tapped to end flow * */
  action_id?: string;
}

/**
 * Corresponding response for `EndFlowRequest`. Note that this call is intended
 * as a “fire and forget” side effect after the flow has ended, so do not use this
 * to send any new blockers.
 */
export interface EndFlowResponse {
  response_context?: ResponseContext;
}

/** Sent by apps to update whether or not a customer has app lock activated or not */
export interface SetAppLockActivatedRequest {
  request_context?: RequestContext;
  app_lock_activated?: boolean;
  passcode?: string;
  passcode_token?: string;
}

/** Sent by server in response to SetAppLockActivatedRequest */
export interface SetAppLockActivatedResponse {
  response_context?: ResponseContext;
  status?: SetAppLockActivatedResponseStatus;
  /**
   * A token that can be supplied when initiating a payment to skip passcode confirmation for
   * that payment.
   */
  passcode_token?: string;
}

export enum SetAppLockActivatedResponseStatus {
  INVALID = "INVALID",
  SUCCESS = "SUCCESS",
  /**
   * INVALID_PASSCODE_TOKEN - The passcode/enclave token is invalid.
   * The use should retry with the correct biometrics or passcode
   */
  INVALID_PASSCODE_TOKEN = "INVALID_PASSCODE_TOKEN",
  /** INVALID_PASSCODE - The passcode is invalid. The user should retry with the correct passcode. */
  INVALID_PASSCODE = "INVALID_PASSCODE",
  /**
   * TOO_MANY_ATTEMPTS - The passcode is invalid and the user has exhausted the number of attempts to provide it.
   * The user cannot attempt again for 24 hours.
   */
  TOO_MANY_ATTEMPTS = "TOO_MANY_ATTEMPTS",
  /** OP_NOT_PERMITTED - The operation is not permitted for the given user (e.g. removing passcode in SCA region). */
  OP_NOT_PERMITTED = "OP_NOT_PERMITTED",
}

export interface SelectParticipantRequest {
  request_context?: RequestContext;
  participants?: GroupParticipant[];
}

export interface SelectParticipantResponse {
  response_context?: ResponseContext;
}

export interface MultiCurrencyAmountEntryRequest {
  /** required */
  request_context?: RequestContext;
  /** required (Money(amount = 200_00, currency_code = USD)), refers to the initiator */
  sender_amount?: Money;
  /** required (Money(amount = 4128_78, currency_code = MXN)), refers to the respondent */
  receiver_amount?: Money;
  /** required (SENDER) */
  focused_field?: MultiCurrencyAmountEntryBlockerFocusedCurrencyField;
  /** required (opaque token from blocker reflected back) */
  cost_tier_identifier?: string;
  /** what fees were actually displayed to the initiator. Blended fees should be excluded here / set as $0 amount */
  displayed_fees?: MultiCurrencyAmountEntryBlockerCostTierFee[];
  /** required (the identifier of the `BlockerAction` that the initiator tapped to submit) */
  action_id?: string;
  /** optional (description of the transaction provided by the initiator) */
  note_value?: string;
  /** optional (the initiator's selected instrument) */
  instrument_token?: string;
}

export interface MultiCurrencyAmountEntryResponse {
  response_context?: ResponseContext;
}

export interface ClabeEntryRequest {
  /** required */
  request_context?: RequestContext;
  /** (“0122905210983201”) */
  clabe_number?:
    | string
    | undefined;
  /** (opaque token from blocker) */
  clabe_token?: string | undefined;
}

export interface ClabeEntryResponse {
  /** required */
  response_context?: ResponseContext;
}

export interface MultiCurrencyPaymentReviewRequest {
  /** required */
  request_context?: RequestContext;
  /** required */
  selected_action?: MultiCurrencyPaymentReviewRequestAction;
}

export enum MultiCurrencyPaymentReviewRequestAction {
  EDIT_AMOUNT = "EDIT_AMOUNT",
  EDIT_RECIPIENT_INFO = "EDIT_RECIPIENT_INFO",
  EDIT_DELIVERY_METHOD = "EDIT_DELIVERY_METHOD",
  ACCEPT = "ACCEPT",
}

export interface MultiCurrencyPaymentReviewResponse {
  /** required */
  response_context?: ResponseContext;
}

export interface OverflowOptionPickerRequest {
  /** required */
  request_context?: RequestContext;
  /** required (from OverflowOptionPickerBlocker.Option.option_identifier) */
  selected_option_identifier?: string;
}

export interface OverflowOptionPickerResponse {
  /** required */
  response_context?: ResponseContext;
}

export interface RecipientSelectorRequest {
  /** required */
  request_context?: RequestContext;
  /** required, the selected customer */
  recipient?: UiCustomer;
  /** optional, the selected instrument for the transaction, including potential fees */
  instrument_selection?: InstrumentSelection;
  /** optional, the value of the note text field populated by the initiator */
  note?: string;
  /** required, an id which provides idempotency if a request is retried */
  external_id?: string;
}

export interface RecipientSelectorResponse {
  /** required */
  response_context?: ResponseContext;
}

/**
 * EXPERIMENTAL: Please do not use this proto (SubmitBlockerRequest) as it is part of an ongoing experiment.
 * Unannounced changes will be continually made to this proto.
 *
 * Submits the contents of a blocker to the server.
 * This message is currently used to encapsulate groups of blockers which are submitted together.
 * In the future, it may be used as the envelope for all blocker requests.
 */
export interface SubmitBlockerRequest {
  group_id?: string;
  app_request_context?: SubmitBlockerRequestAppRequestContext;
  requests?: SubmitBlockerRequestRequest[];
}

export interface SubmitBlockerRequestAppRequestContext {
  /**
   * Metadata the server will use to fetch sync entities.
   * Use this field as the source of truth regarding known sync ranges,
   * as the sub-requests in the oneof field will also have all_known_ranges fields.
   */
  all_known_ranges?: Uint8Array[];
}

export interface SubmitBlockerRequestRequest {
  /** This overrides the request_context within each request type below. */
  blocker_descriptor_id?: string;
  /** EmailVerificationBlocker */
  verify_email_request?:
    | VerifyEmailRequest
    | undefined;
  /** PasscodeVerificationBlocker */
  verify_passcode_request?:
    | VerifyPasscodeRequest
    | undefined;
  /** InstrumentVerificationBlocker */
  verify_instrument_request?:
    | VerifyInstrumentRequest
    | undefined;
  /** PasscodeCreationBlocker */
  set_passcode_request?:
    | SetPasscodeRequest
    | undefined;
  /** CardBlocker */
  link_card_request?:
    | LinkCardRequest
    | undefined;
  /** GovernmentIdBlocker */
  verify_government_id_request?:
    | VerifyGovernmentIdRequest
    | undefined;
  /** IdentityVerificationBlocker */
  verify_identity_request?:
    | VerifyIdentityRequest
    | undefined;
  /** RatePlanBlocker */
  set_rate_plan_request?:
    | SetRatePlanRequest
    | undefined;
  /** PhoneVerificationBlocker */
  verify_sms_request?:
    | VerifySmsRequest
    | undefined;
  /** ConfirmBlocker */
  confirm_payment_request?:
    | ConfirmPaymentRequest
    | undefined;
  /** NameBlocker */
  set_full_name_request?:
    | SetFullNameRequest
    | undefined;
  /** CashtagBlocker */
  set_cashtag_request?:
    | SetCashtagRequest
    | undefined;
  /** ResolveMergeBlocker */
  resolve_merge_request?:
    | ResolveMergeRequest
    | undefined;
  /** RewardCodeBlocker */
  apply_reward_code_request?:
    | ApplyRewardCodeRequest
    | undefined;
  /** AddressBlocker */
  set_address_request?:
    | SetAddressRequest
    | undefined;
  /**
   * SelectionBlocker
   * ApplePaySelectionBlocker
   */
  select_option_request?:
    | SelectOptionRequest
    | undefined;
  /** ApplyPayProvisioningBlocker */
  provision_apple_pay_request?:
    | ProvisionApplePayRequest
    | undefined;
  /** SignatureBlocker */
  set_signature_request?:
    | SetSignatureRequest
    | undefined;
  /** QrCodeBlocker */
  verify_qr_code_request?:
    | VerifyQrCodeRequest
    | undefined;
  /** CardPasscodeAndExpirationBlocker */
  verify_passcode_and_expiration_request?:
    | VerifyPasscodeAndExpirationRequest
    | undefined;
  /** FileBlocker */
  upload_file_request?:
    | UploadFileRequest
    | undefined;
  /** ContactVerificationBlocker */
  verify_contacts_request?:
    | VerifyContactsRequest
    | undefined;
  /** RegionBlocker */
  set_country_request?:
    | SetCountryRequest
    | undefined;
  /** ScheduledTransactionBlocker */
  set_scheduled_transaction_preference_request?:
    | SetScheduledTransactionPreferenceRequest
    | undefined;
  /** GooglePayProvisioningBlocker */
  provision_digital_wallet_token_request?: ProvisionDigitalWalletTokenRequest | undefined;
  activate_digital_wallet_request?:
    | ActivateDigitalWalletRequest
    | undefined;
  /** CardCustomizationBlocker */
  set_card_customization_request?:
    | SetCardCustomizationRequest
    | undefined;
  /** OrderConfirmationBlocker */
  order_confirmation_request?:
    | OrderConfirmationRequest
    | undefined;
  /**
   * FormBlocker
   * WebviewBlocker
   */
  submit_form_request?:
    | SubmitFormRequest
    | undefined;
  /** DisclosureBlocker */
  confirm_disclosure_request?:
    | ConfirmDisclosureRequest
    | undefined;
  /** AliasBlocker */
  register_sms_request?: RegisterSmsRequest | undefined;
  register_email_request?:
    | RegisterEmailRequest
    | undefined;
  /** CheckDepositBlocker */
  check_deposit_request?:
    | CheckDepositRequest
    | undefined;
  /** ThreeDomainSecureRedirectBlocker */
  handle_three_domain_secure_redirect_request?:
    | HandleThreeDomainSecureRedirectRequest
    | undefined;
  /** AmountBlocker */
  set_amount_request?:
    | SetAmountRequest
    | undefined;
  /** GooglePayCompleteProvisioningBlocker */
  complete_digital_wallet_token_provisioning_request?:
    | CompleteDigitalWalletTokenProvisioningRequest
    | undefined;
  /**
   * TransactionPickerBlocker
   * ActivityPickerBlocker
   */
  select_activity_request?:
    | SelectActivityRequest
    | undefined;
  /** ThreeDomainSecureV2Blocker */
  handle_three_domain_secure_v2_action_request?:
    | HandleThreeDomainSecureV2ActionRequest
    | undefined;
  /** SelectSponsorsBlocker */
  select_sponsors_request?:
    | SelectSponsorsRequest
    | undefined;
  /** PinwheelLinkBlocker */
  finish_pinweel_link_request?:
    | FinishPinwheelLinkRequest
    | undefined;
  /** PasswordCreationBlocker */
  set_password_request?:
    | SetPasswordRequest
    | undefined;
  /** PasswordVerificationBlocker */
  verify_password_request?:
    | VerifyPasswordRequest
    | undefined;
  /** TutorialBlocker */
  finish_tutorial_request?:
    | FinishTutorialRequest
    | undefined;
  /** PayrollProviderSearchBlocker */
  submit_payroll_provider_selection_request?:
    | SubmitPayrollProviderSelectionRequest
    | undefined;
  /** InstantPayAutoAdvanceBlocker */
  finish_instant_pay_auto_advance_request?:
    | FinishInstantPayAutoAdvanceRequest
    | undefined;
  /** PersonaDidvBlocker */
  resolve_persona_didv_blocker_request?:
    | ResolvePersonaDidvBlockerRequest
    | undefined;
  /**
   * RoundUpOnboardingBlocker
   * ChangeRoundUpDestinationBlocker
   */
  submit_automation_for_flow_request?:
    | SubmitAutomationForFlowRequest
    | undefined;
  /** ApplePayCompleteProvisioningBlocker */
  complete_apple_pay_provisioning_request?:
    | CompleteApplePayProvisioningRequest
    | undefined;
  /** FilesetUploadBlocker */
  submit_fileset_request?:
    | SubmitFilesetRequest
    | undefined;
  /** InstrumentSelectionBlocker */
  select_instrument_request?:
    | SelectInstrumentRequest
    | undefined;
  /** InstantPayDirectDepositSwitchBlocker */
  finish_instant_pay_direct_deposit_switch_request?:
    | FinishInstantPayDirectDepositSwitchRequest
    | undefined;
  /** DirectDepositSetupBlocker */
  direct_deposit_transfer_selection_request?:
    | DirectDepositTransferSelectionRequest
    | undefined;
  /** ParticipantSelectionBlocker */
  select_participant_request?:
    | SelectParticipantRequest
    | undefined;
  /** CreditMultiStepLoadingBlocker */
  finish_credit_multi_step_load_request?: FinishCreditMultiStepLoadRequest | undefined;
  credit_multi_step_polling_request?:
    | CreditMultiStepLoadPollingRequest
    | undefined;
  /** LoanAmountPicker */
  loan_amount_picker_request?:
    | LoanAmountPickerRequest
    | undefined;
  /** SelectInvestmentEntityBlocker */
  submit_investment_entity_selection_request?: SubmitInvestmentEntitySelectionRequest | undefined;
}

/**
 * EXPERIMENTAL: Please do not use this proto (SubmitBlockerResponse) as it is part of an ongoing experiment.
 * Unannounced changes will be continually made to this proto.
 *
 * Sent by the server in response to a SubmitBlockerRequest.
 */
export interface SubmitBlockerResponse {
  app_response_context?: SubmitBlockerResponseAppResponseContext;
  blocker_response_context?: SubmitBlockerResponseBlockerResponseContext;
  flow_response_context?: SubmitBlockerResponseFlowResponseContext;
}

export interface SubmitBlockerResponseAppResponseContext {
  profile?: Profile;
  sync_entities_data?: SyncEntitiesResponse;
}

export interface SubmitBlockerResponseBlockerResponseContext {
  dialog?: SubmitBlockerResponseBlockerResponseContextDialog;
}

/** TODO (doh): promote this proto to a shared definition */
export interface SubmitBlockerResponseBlockerResponseContextDialog {
  title?: string;
  message?: string;
}

export interface SubmitBlockerResponseFlowResponseContext {
  scenario_plan?: ScenarioPlan;
}

export interface AppService {
  InitiateSession(request: InitiateSessionRequest): Promise<InitiateSessionResponse>;
  RegisterSms(request: RegisterSmsRequest): Promise<RegisterSmsResponse>;
  VerifySms(request: VerifySmsRequest): Promise<VerifySmsResponse>;
  VerifyPasscode(request: VerifyPasscodeRequest): Promise<VerifyPasscodeResponse>;
  VerifyInstrument(request: VerifyInstrumentRequest): Promise<VerifyInstrumentResponse>;
  MatchContacts(request: MatchContactsRequest): Promise<MatchContactsResponse>;
  SyncContacts(request: SyncContactsRequest): Promise<SyncContactsResponse>;
  VerifyContacts(request: VerifyContactsRequest): Promise<VerifyContactsResponse>;
  RefreshSession(request: RefreshSessionRequest): Promise<RefreshSessionResponse>;
  InitiatePayment(request: InitiatePaymentRequest): Promise<InitiatePaymentResponse>;
  UpdatePaymentSchedule(request: UpdatePaymentScheduleRequest): Promise<UpdatePaymentScheduleResponse>;
  LinkCard(request: LinkCardRequest): Promise<LinkCardResponse>;
  UnlinkCard(request: UnlinkCardRequest): Promise<UnlinkCardResponse>;
  UnlinkInstrument(request: UnlinkInstrumentRequest): Promise<UnlinkInstrumentResponse>;
  LinkBankAccount(request: LinkBankAccountRequest): Promise<LinkBankAccountResponse>;
  UnlinkBankAccount(request: UnlinkBankAccountRequest): Promise<UnlinkBankAccountResponse>;
  OcrCard(request: OcrCardRequest): Promise<OcrCardResponse>;
  ConfirmOrder(request: OrderConfirmationRequest): Promise<OrderConfirmationResponse>;
  ConfirmPasscode(request: ConfirmPasscodeRequest): Promise<ConfirmPasscodeResponse>;
  ConfirmPayment(request: ConfirmPaymentRequest): Promise<ConfirmPaymentResponse>;
  CancelPayment(request: CancelPaymentRequest): Promise<CancelPaymentResponse>;
  RefundPayment(request: RefundPaymentRequest): Promise<RefundPaymentResponse>;
  AddReaction(request: AddReactionRequest): Promise<AddReactionResponse>;
  VerifyIdentity(request: VerifyIdentityRequest): Promise<VerifyIdentityResponse>;
  GetProfile(request: GetProfileRequest): Promise<GetProfileResponse>;
  GetThreadedViewData(request: GetThreadedViewDataRequest): Promise<GetThreadedViewDataResponse>;
  GetConversationList(request: GetConversationListRequest): Promise<GetConversationListResponse>;
  GetServiceMessage(request: GetServiceMessageRequest): Promise<GetServiceMessageResponse>;
  SetFullName(request: SetFullNameRequest): Promise<SetFullNameResponse>;
  SetNearbyPrivacyPreference(request: SetNearbyPrivacyPreferenceRequest): Promise<SetNearbyPrivacyPreferenceResponse>;
  SendDownloadLink(request: SendDownloadLinkRequest): Promise<SendDownloadLinkResponse>;
  RegisterEmail(request: RegisterEmailRequest): Promise<RegisterEmailResponse>;
  VerifyEmail(request: VerifyEmailRequest): Promise<VerifyEmailResponse>;
  UnregisterAlias(request: UnregisterAliasRequest): Promise<UnregisterAliasResponse>;
  GetNearbyToken(request: GetNearbyTokenRequest): Promise<GetNearbyTokenResponse>;
  GetPlatformClient(request: GetPlatformClientRequest): Promise<GetPlatformClientResponse>;
  ApprovePlatformClient(request: ApprovePlatformClientRequest): Promise<ApprovePlatformClientResponse>;
  LookupCustomer(request: LookupCustomerRequest): Promise<LookupCustomerResponse>;
  SetProfilePhoto(request: SetProfilePhotoRequest): Promise<SetProfilePhotoResponse>;
  ClearProfilePhoto(request: ClearProfilePhotoRequest): Promise<ClearProfilePhotoResponse>;
  SetNotificationPreference(request: SetNotificationPreferenceRequest): Promise<SetNotificationPreferenceResponse>;
  SetRequirePasscodeConfirmation(
    request: SetRequirePasscodeConfirmationRequest,
  ): Promise<SetRequirePasscodeConfirmationResponse>;
  SetPasscode(request: SetPasscodeRequest): Promise<SetPasscodeResponse>;
  GetCashtagProfile(request: GetCashtagProfileRequest): Promise<GetCashtagProfileResponse>;
  GetCashtagStatus(request: GetCashtagStatusRequest): Promise<GetCashtagStatusResponse>;
  SetCashtag(request: SetCashtagRequest): Promise<SetCashtagResponse>;
  SetCashtagUrlEnabled(request: SetCashtagUrlEnabledRequest): Promise<SetCashtagUrlEnabledResponse>;
  GetCashtagSuggestion(request: GetCashtagSuggestionRequest): Promise<GetCashtagSuggestionResponse>;
  RegisterDevice(request: RegisterDeviceRequest): Promise<RegisterDeviceResponse>;
  SignOut(request: SignOutRequest): Promise<SignOutResponse>;
  ResetBadge(request: ResetBadgeRequest): Promise<ResetBadgeResponse>;
  TriggerWelcomeBonus(request: TriggerWelcomeBonusRequest): Promise<TriggerWelcomeBonusResponse>;
  ClaimByPaymentToken(request: ClaimByPaymentTokenRequest): Promise<ClaimByPaymentTokenResponse>;
  GetPayment(request: GetPaymentRequest): Promise<GetPaymentResponse>;
  GetTransaction(request: GetTransactionRequest): Promise<GetTransactionResponse>;
  GetAppConfig(request: GetAppConfigRequest): Promise<GetAppConfigResponse>;
  GetInvitationConfig(request: GetInvitationConfigRequest): Promise<GetInvitationConfigResponse>;
  RegisterInvitations(request: RegisterInvitationsRequest): Promise<RegisterInvitationsResponse>;
  InviteContacts(request: InviteContactsRequest): Promise<InviteContactsResponse>;
  CompleteInviteFriends(request: CompleteInviteFriendsRequest): Promise<CompleteInviteFriendsResponse>;
  CheckVersion(request: CheckVersionRequest): Promise<CheckVersionResponse>;
  DeleteContacts(request: DeleteContactsRequest): Promise<DeleteContactsResponse>;
  SetRatePlan(request: SetRatePlanRequest): Promise<SetRatePlanResponse>;
  FindCustomers(request: FindCustomersRequest): Promise<FindCustomersResponse>;
  GetScenarioPlan(request: GetScenarioPlanRequest): Promise<GetScenarioPlanResponse>;
  ResolveMerge(request: ResolveMergeRequest): Promise<ResolveMergeResponse>;
  ReportSupportFlowActivity(request: ReportSupportFlowActivityRequest): Promise<ReportSupportFlowActivityResponse>;
  /** DEPRECATED */
  GetSuggestedContacts(request: GetSuggestedContactsRequest): Promise<GetSuggestedContactsResponse>;
  GetAppMessage(request: GetAppMessageRequest): Promise<GetAppMessageResponse>;
  GetAppMessageTemplatesApp(request: GetAppMessageTemplatesRequest): Promise<GetAppMessageTemplatesResponse>;
  RegisterAppMessageAction(request: RegisterAppMessageActionRequest): Promise<RegisterAppMessageActionResponse>;
  SetAppMessagePreference(request: SetAppMessagePreferenceRequest): Promise<SetAppMessagePreferenceResponse>;
  CheckRewardCode(request: CheckRewardCodeRequest): Promise<CheckRewardCodeResponse>;
  ApplyRewardCode(request: ApplyRewardCodeRequest): Promise<ApplyRewardCodeResponse>;
  GetRewardStatus(request: GetRewardStatusRequest): Promise<GetRewardStatusResponse>;
  GetPaymentRewardStatus(request: GetPaymentRewardStatusRequest): Promise<GetPaymentRewardStatusResponse>;
  SyncEntities(request: SyncEntitiesRequest): Promise<SyncEntitiesResponse>;
  GetPagedSyncEntities(request: GetPagedSyncEntitiesRequest): Promise<GetPagedSyncEntitiesResponse>;
  VerifyCheckAddress(request: VerifyCheckAddressRequest): Promise<VerifyCheckAddressResponse>;
  SetCountry(request: SetCountryRequest): Promise<SetCountryResponse>;
  GetStatement(request: GetStatementRequest): Promise<GetStatementResponse>;
  CreateCheckRecipient(request: CreateCheckRecipientRequest): Promise<CreateCheckRecipientResponse>;
  GetCheckAddress(request: GetCheckAddressRequest): Promise<GetCheckAddressResponse>;
  UpdateCheckRecipient(request: UpdateCheckRecipientRequest): Promise<UpdateCheckRecipientResponse>;
  SupplyCheckRecipientContacts(
    request: SupplyCheckRecipientContactsRequest,
  ): Promise<SupplyCheckRecipientContactsResponse>;
  CreateSupportCase(request: CreateSupportCaseRequest): Promise<CreateSupportCaseResponse>;
  SetDepositPreference(request: SetDepositPreferenceRequest): Promise<SetDepositPreferenceResponse>;
  VerifyGovernmentId(request: VerifyGovernmentIdRequest): Promise<VerifyGovernmentIdResponse>;
  TransferFunds(request: TransferFundsRequest): Promise<TransferFundsResponse>;
  GetTransfer(request: GetTransferRequest): Promise<GetTransferResponse>;
  GetIssuedCard(request: GetIssuedCardRequest): Promise<GetIssuedCardResponse>;
  SetIssuedCardEnabled(request: SetIssuedCardEnabledRequest): Promise<SetIssuedCardEnabledResponse>;
  SetIssuedCardLock(request: SetIssuedCardLockRequest): Promise<SetIssuedCardLockResponse>;
  SetCampaignSupporterData(request: SetCampaignSupporterDataRequest): Promise<SetCampaignSupporterDataResponse>;
  VerifyDevice(request: VerifyDeviceRequest): Promise<VerifyDeviceResponse>;
  ProvisionApplePay(request: ProvisionApplePayRequest): Promise<ProvisionApplePayResponse>;
  CompleteApplePayProvisioning(
    request: CompleteApplePayProvisioningRequest,
  ): Promise<CompleteApplePayProvisioningResponse>;
  ProvisionDigitalWalletToken(
    request: ProvisionDigitalWalletTokenRequest,
  ): Promise<ProvisionDigitalWalletTokenResponse>;
  CompleteDigitalWalletTokenProvisioning(
    request: CompleteDigitalWalletTokenProvisioningRequest,
  ): Promise<CompleteDigitalWalletTokenProvisioningResponse>;
  ActivateDigitalWallet(request: ActivateDigitalWalletRequest): Promise<ActivateDigitalWalletResponse>;
  InitiatePasscodeReset(request: InitiatePasscodeResetRequest): Promise<InitiatePasscodeResetResponse>;
  SetAddress(request: SetAddressRequest): Promise<SetAddressResponse>;
  SelectOption(request: SelectOptionRequest): Promise<SelectOptionResponse>;
  SubmitForm(request: SubmitFormRequest): Promise<SubmitFormResponse>;
  FinishPinwheelLink(request: FinishPinwheelLinkRequest): Promise<FinishPinwheelLinkResponse>;
  AuthenticateInstitution(request: AuthenticateInstitutionRequest): Promise<AuthenticateInstitutionResponse>;
  VerifyMultifactorAuthentication(
    request: VerifyMultifactorAuthenticationRequest,
  ): Promise<VerifyMultifactorAuthenticationResponse>;
  SelectInstitutionAccount(request: SelectInstitutionAccountRequest): Promise<SelectInstitutionAccountResponse>;
  CloseAccount(request: CloseAccountRequest): Promise<CloseAccountResponse>;
  GetWebLoginConfig(request: GetWebLoginConfigRequest): Promise<GetWebLoginConfigResponse>;
  ReportAbuse(request: ReportAbuseRequest): Promise<ReportAbuseResponse>;
  CompleteScenario(request: CompleteScenarioRequest): Promise<CompleteScenarioResponse>;
  FlowComplete(request: FlowCompleteRequest): Promise<FlowCompleteResponse>;
  SkipBlocker(request: SkipBlockerRequest): Promise<SkipBlockerResponse>;
  ConfirmIosDonation(request: ConfirmIosDonationRequest): Promise<ConfirmIosDonationResponse>;
  StartSupportFlow(request: StartSupportFlowRequest): Promise<StartSupportFlowResponse>;
  AdvanceSupportFlow(request: AdvanceSupportFlowRequest): Promise<AdvanceSupportFlowResponse>;
  GetSupportContactStatus(request: GetSupportContactStatusRequest): Promise<GetSupportContactStatusResponse>;
  SetCardCustomization(request: SetCardCustomizationRequest): Promise<SetCardCustomizationResponse>;
  VerifyQrCode(request: VerifyQrCodeRequest): Promise<VerifyQrCodeResponse>;
  VerifyPasscodeAndExpiration(
    request: VerifyPasscodeAndExpirationRequest,
  ): Promise<VerifyPasscodeAndExpirationResponse>;
  VerifyMagicLink(request: VerifyMagicLinkRequest): Promise<VerifyMagicLinkResponse>;
  GetUnredactedIssuedCard(request: GetUnredactedIssuedCardRequest): Promise<GetUnredactedIssuedCardResponse>;
  GetExchangeData(request: GetExchangeDataRequest): Promise<GetExchangeDataResponse>;
  GetHistoricalExchangeData(request: GetHistoricalExchangeDataRequest): Promise<GetHistoricalExchangeDataResponse>;
  GetExchangeContract(request: GetExchangeContractRequest): Promise<GetExchangeContractResponse>;
  ExecuteContract(request: ExecuteContractRequest): Promise<ExecuteContractResponse>;
  InitiateCryptocurrencyTransfer(
    request: InitiateCryptocurrencyTransferRequest,
  ): Promise<InitiateCryptocurrencyTransferResponse>;
  UploadFile(request: UploadFileRequest): Promise<UploadFileResponse>;
  GetFeatureFlags(request: GetFeatureFlagsRequest): Promise<GetFeatureFlagsResponse>;
  SetFeatureFlags(request: SetFeatureFlagsRequest): Promise<SetFeatureFlagsResponse>;
  GetEffectiveCustomerLimits(request: GetEffectiveCustomerLimitsRequest): Promise<GetEffectiveCustomerLimitsResponse>;
  AddOrUpdateReward(request: AddOrUpdateRewardRequest): Promise<AddOrUpdateRewardResponse>;
  UnlockReward(request: UnlockRewardRequest): Promise<UnlockRewardResponse>;
  SetScheduledTransactionPreference(
    request: SetScheduledTransactionPreferenceRequest,
  ): Promise<SetScheduledTransactionPreferenceResponse>;
  SetLoyaltyNotificationPreference(
    request: SetLoyaltyNotificationPreferenceRequest,
  ): Promise<SetLoyaltyNotificationPreferenceResponse>;
  GetEquityContract(request: GetEquityContractRequest): Promise<GetEquityContractResponse>;
  ExecuteEquityContract(request: ExecuteEquityContractRequest): Promise<ExecuteEquityContractResponse>;
  GetRewardProfile(request: GetRewardProfileRequest): Promise<GetRewardProfileResponse>;
  SuggestedRecipients(request: SuggestedRecipientsRequest): Promise<SuggestedRecipientsResponse>;
  GetTaxFormPrefill(request: GetTaxFormPrefillRequest): Promise<GetTaxFormPrefillResponse>;
  ShareReceipt(request: ShareReceiptRequest): Promise<ShareReceiptResponse>;
  SetTaxInformation(request: SetTaxInformationRequest): Promise<SetTaxInformationResponse>;
  OAuthResolveFlow(request: OAuthResolveFlowRequest): Promise<OAuthResolveFlowResponse>;
  ResetDevice(request: ResetDeviceRequest): Promise<ResetDeviceResponse>;
  ReportDeeplink(request: ReportDeeplinkRequest): Promise<ReportDeeplinkResponse>;
  SetIncomingRequestPolicy(request: SetIncomingRequestPolicyRequest): Promise<SetIncomingRequestPolicyResponse>;
  GetLocationPermission(request: GetLocationPermissionRequest): Promise<GetLocationPermissionResponse>;
  SetLocationPermission(request: SetLocationPermissionRequest): Promise<SetLocationPermissionResponse>;
  GetLocationConfig(request: GetLocationConfigRequest): Promise<GetLocationConfigResponse>;
  PublishGpsEvent(request: PublishGpsEventRequest): Promise<PublishGpsEventResponse>;
  TriggerBalanceCheck(request: TriggerBalanceCheckRequest): Promise<TriggerBalanceCheckResponse>;
  CreateRedPocketEnvelope(request: CreateRedPocketEnvelopeRequest): Promise<CreateRedPocketEnvelopeResponse>;
  OpenRedPocketEnvelope(request: OpenRedPocketEnvelopeRequest): Promise<OpenRedPocketEnvelopeResponse>;
  InitiateRemoteCheckDeposit(request: InitiateRemoteCheckDepositRequest): Promise<InitiateRemoteCheckDepositResponse>;
  ConfirmDisclosure(request: ConfirmDisclosureRequest): Promise<ConfirmDisclosureResponse>;
  SetIndividualTaxInformation(
    request: SetIndividualTaxInformationRequest,
  ): Promise<SetIndividualTaxInformationResponse>;
  ConfirmIndividualTaxInformation(
    request: ConfirmIndividualTaxInformationRequest,
  ): Promise<ConfirmIndividualTaxInformationResponse>;
  GetIndividualTaxInformationStatus(
    request: GetIndividualTaxInformationStatusRequest,
  ): Promise<GetIndividualTaxInformationStatusResponse>;
  GetClaimablePayment(request: GetClaimablePaymentRequest): Promise<GetClaimablePaymentResponse>;
  HandleThreeDSChallengeOutcome(
    request: HandleThreeDSChallengeOutcomeRequest,
  ): Promise<HandleThreeDSChallengeOutcomeResponse>;
  ApproveAccountIntegration(request: ApproveAccountIntegrationRequest): Promise<ApproveAccountIntegrationResponse>;
  GetActiveIntegrations(request: GetActiveIntegrationsRequest): Promise<GetActiveIntegrationsResponse>;
  RevokeAccountIntegration(request: RevokeAccountIntegrationRequest): Promise<RevokeAccountIntegrationResponse>;
  GetEnrollment(request: GetEnrollmentRequest): Promise<GetEnrollmentResponse>;
  SendTaxFormEmail(request: SendTaxFormEmailRequest): Promise<SendTaxFormEmailResponse>;
  SetBitcoinDisplayPreference(
    request: SetBitcoinDisplayPreferenceRequest,
  ): Promise<SetBitcoinDisplayPreferenceResponse>;
  GetFlow(request: GetFlowRequest): Promise<GetFlowResponse>;
  DepositCheck(request: CheckDepositRequest): Promise<CheckDepositResponse>;
  HandleThreeDomainSecureRedirect(
    request: HandleThreeDomainSecureRedirectRequest,
  ): Promise<HandleThreeDomainSecureRedirectResponse>;
  HandleThreeDomainSecureV2Action(
    request: HandleThreeDomainSecureV2ActionRequest,
  ): Promise<HandleThreeDomainSecureV2ActionResponse>;
  SetSignature(request: SetSignatureRequest): Promise<SetSignatureResponse>;
  SetAmount(request: SetAmountRequest): Promise<SetAmountResponse>;
  CashIn(request: CashInRequest): Promise<CashInResponse>;
  SelectSponsors(request: SelectSponsorsRequest): Promise<SelectSponsorsResponse>;
  FinishTutorial(request: FinishTutorialRequest): Promise<FinishTutorialResponse>;
  SetOnboardingContext(request: SetOnboardingContextRequest): Promise<SetOnboardingContextResponse>;
  FinishInstantPayAutoAdvance(
    request: FinishInstantPayAutoAdvanceRequest,
  ): Promise<FinishInstantPayAutoAdvanceResponse>;
  FinishInstantPayDirectDepositSwitch(
    request: FinishInstantPayDirectDepositSwitchRequest,
  ): Promise<FinishInstantPayDirectDepositSwitchResponse>;
  UnlinkBusiness(request: UnlinkBusinessRequest): Promise<UnlinkBusinessResponse>;
  ApproveCashAppPay(request: ApproveCashAppPayRequest): Promise<ApproveCashAppPayResponse>;
  PlaidLinkTokenCreate(request: PlaidLinkTokenCreateRequest): Promise<PlaidLinkTokenCreateResponse>;
  ResolvePersonaDidv(request: ResolvePersonaDidvBlockerRequest): Promise<ResolvePersonaDidvBlockerResponse>;
  SelectActivity(request: SelectActivityRequest): Promise<SelectActivityResponse>;
  SubmitFileset(request: SubmitFilesetRequest): Promise<SubmitFilesetResponse>;
  EndFlow(request: EndFlowRequest): Promise<EndFlowResponse>;
  SetAppLockActivated(request: SetAppLockActivatedRequest): Promise<SetAppLockActivatedResponse>;
  SelectParticipants(request: SelectParticipantRequest): Promise<SelectParticipantResponse>;
  SubmitBlocker(request: SubmitBlockerRequest): Promise<SubmitBlockerResponse>;
  FinishCreditMultiStepLoad(request: FinishCreditMultiStepLoadRequest): Promise<FinishCreditMultiStepLoadResponse>;
  CreditMultiStepPoll(request: CreditMultiStepLoadPollingRequest): Promise<CreditMultiStepLoadPollingResponse>;
  CompleteLoanAmountSelection(request: LoanAmountPickerRequest): Promise<LoanAmountPickerResponse>;
  CreditNullStateButton(request: CreditNullStateButtonRequest): Promise<CreditNullStateButtonResponse>;
  CreditFirstTimeBorrow(request: CreditFirstTimeBorrowRequest): Promise<CreditFirstTimeBorrowResponse>;
}
